import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { Modal } from "../ui/modal";
import type {
  Appointment,
  BlockReason,
  Chair,
  DayRoster,
  ExternalBlock,
  Patient,
  ProcedureCode,
  ProviderAvailability,
  RefusedOccurrence,
  RefusedVisit,
  StaffMember,
  TreatmentPlan,
  WaitlistEntry,
} from "@dentacore/shared-types";
import type {
  CalendarStatusGroup,
  CombinedCalendar,
  CombinedLocation,
  CombinedMonth,
  ProcedureCategory,
} from "@dentacore/shared-types";
import {
  CALENDAR_STATUS_GROUPS,
  CALENDAR_STATUS_GROUP_LABELS,
  FIRST_VISIT_TYPE,
  NO_CATEGORY_TYPE,
  appointmentTypeOf,
  isPlanOpen,
  passesFilters,
  statusGroupOf,
} from "@dentacore/shared-types";

import {
  createAppointment,
  getRoster,
  listAppointments,
  listExternalBlocks,
  listChairs,
  listProviderAvailability,
  listStaff,
  moveAppointment,
} from "../api/appointments";
import { APPOINTMENTS_CHANGED } from "../api/appointments-changed";
import { listBlockReasons } from "../api/block-reasons";
import { getPatient, searchPatients } from "../api/patients";
import { listProcedureCodes } from "../api/catalogue";
import { useActiveBranch } from "../api/use-active-branch";
import { useBranchTimezoneOrNull } from "../api/use-branch-timezone";
import { useHasBranches } from "../api/use-has-branches";
import { useCan } from "../auth/use-can";
import { AppointmentPanel } from "./appointment-panel";
import { DayGrid } from "./day-grid";
import { EditChairs } from "./edit-chairs";
import {
  SNAP_MINUTES,
  blocksTheChair,
  clockOfMinutes,
  isMovable,
  minutesOfClock,
} from "./grid-geometry";
import { NewPatientForm } from "../patients/new-patient-form";
import { RequestQueue } from "./request-queue";
import { useModules } from "../shell/modules-context";
import { useWaiting } from "../shell/waiting-context";
import { HelpTip, RowMenu } from "../ui/popovers";
import { ReschedulePicker } from "./reschedule-picker";
import { WaitlistStrip } from "./waitlist-strip";
import { browserTimeZone, instantOfWallClock, todayIn, wallClockOfInstant } from "./zoned-time";
import { bookChain, linkPart, listPlans, shiftChain, startCourse } from "../api/plans";
import { CriticalAlerts } from "../patients/critical-alerts";
import { ApiError, atLocation } from "../api/client";
import { getCombined, getCombinedMonth } from "../api/calendar";
import { listProcedureCategories } from "../api/procedure-categories";
import { useAuth } from "../auth/auth-context";
import { categoryColour } from "../plans/categories-dialog";
import { BookingLocationProvider } from "./booking-location";
import { useCalendarLocations } from "./calendar-selection";
import { CombinedDayGrid } from "./combined-day-grid";
import type { CombinedColumns } from "./combined-day-grid";
import type { Capacity } from "./hour-window";
import {
  canZoomOut,
  effectiveHours,
  isPreset,
  PRESETS,
  stepZoom,
  useHoursShown,
  zoomLabel,
  ZOOM_STEPS,
} from "./hour-window";
import { CombinedMonthView } from "./combined-month";
import { CombinedWeek } from "./combined-week";
import { FilterBar } from "./filter-panel";
import type { FilterLocation, FilterOption } from "./filter-panel";
import { LocationBadge } from "./location-badge";
import { useCalendarFilters } from "./use-calendar-filters";
import type { FilterGroup } from "./use-calendar-filters";
import dayStyles from "./day-grid.module.css";
import { ChainVisits, useChainRows } from "./chain-visits";
import { ToothPicker } from "./tooth-picker";
import { FamilyMembers, ProcedureTags } from "./family-members";
import { groupVisits } from "./group-visits";
import type { FamilyMember } from "./family-members";
import { ShiftDialog } from "./shift-dialog";
import type { ChainVisit } from "./chain-visits";
import { Select } from "../ui/select";
import styles from "./calendar.module.css";
import { RequiredMark } from "../ui/required-mark";
import { useLiveRefresh } from "../api/live";
import { useRingProvider } from "../shell/ring/ring-registry";
import { RING_ICONS } from "../shell/ring/ring-icons";
import { beforeArrival, visitStep } from "./ring-steps";
import type { PanelRingCommand, PanelRingKey } from "./ring-steps";
import { nextStatuses, statusLabel } from "./status-graph";

/**
 * Who may be offered as the provider on a booking (QA #16).
 *
 * The glossary's definition: *"a clinician who performs treatment — dentist or
 * hygienist"*. Mirrors `CLINICAL_ROLES` in the API, which now refuses anything
 * else on write — this is the half that stops the picker offering it.
 */
const CLINICAL_ROLES = new Set(["DENTIST", "HYGIENIST"]);

/**
 * The one row in the "For" list that is not a procedure: it opens the free-text
 * box. A sentinel rather than the empty value, so an unanswered field shows its
 * placeholder instead of reading as though "Something else" had been chosen.
 */
const DESCRIBE_IT = "__describe__";

/** No remaining visits — one array, so the proposal hook sees no change. */
const NO_CHAIN: readonly ChainVisit[] = [];

/**
 * The calendar: **the day grid, and the form that books into it.**
 *
 * STORY-032 replaced STORY-029b's plain list with `DayGrid` — one column per
 * chair, minute-accurate, the now-line on the branch's own clock. The booking
 * form is what survived, unchanged, which was the point of building it first.
 *
 * This component fetches and holds; it draws almost nothing. The day's three
 * reads — chairs, rota and appointments — live here because the grid and the
 * rota editor both need the rota, and one owner beats two requests.
 *
 * ── Nothing here decides whether a slot is legal ────────────────────────────
 *
 * Every refusal comes from the server, which asks `ConflictService` (STORY-029).
 * A screen that pre-checked would be a second implementation of the turnover
 * rule, and the two would disagree the first time either changed — on a
 * clinician's booking, at the moment a patient is on the phone.
 */
export function CalendarPage(): React.JSX.Element {
  // **Null until the branch switcher resolves**, which is the normal state on a
  // first sign-in. Everything branch-scoped waits for it rather than firing a
  // request with no header and reading the refusal as an empty list.
  const branchId = useActiveBranch();
  // Tells the two nothings apart — see the empty state near the bottom of this file.
  const hasBranches = useHasBranches();
  const can = useCan();
  const navigate = useNavigate();
  const hasChatbot = useModules().has("CHATBOT");
  /**
   * **Today at the location, not at the browser** (P2 Q8, QA third run). The
   * zone is cached once any screen has read it; on a cold open the browser's
   * stands in until the location's arrives, and the opening day follows it
   * then — unless somebody has already moved off it.
   */
  const knownZone = useBranchTimezoneOrNull();
  const today = (): string => todayIn(knownZone ?? browserTimeZone());
  const [day, setDay] = useState(today);
  const guessedToday = useRef(day);
  /**
   * The native date input behind the title — visually near-invisible, but a
   * real `<input type="date">` so the browser's own picker, keyboard
   * behaviour and accessibility come for free rather than being rebuilt.
   */
  const dateInputRef = useRef<HTMLInputElement>(null);

  /**
   * Which view is on screen. The mockup offers Day, Week and Month; Month is
   * STORY-042, and this switcher grows one option when it lands.
   */
  const [view, setView] = useState<"day" | "week" | "month">("day");
  /** The week as rows by days (CF-D13) — read through the combined route for one location too. */
  const [week, setWeek] = useState<CombinedCalendar | null>(null);

  /**
   * **Which locations are shown** (CF-D1, CF-D12), chosen in the top bar and
   * remembered on the account. One is today's single calendar; several are one
   * calendar with doctor columns, or chairs on the switch.
   */
  const calendarLocations = useCalendarLocations();
  const shownIds = calendarLocations.shown;
  const combined = shownIds.length > 1;
  const shownKey = shownIds.join(",");
  const [columnsBy, setColumnsBy] = useState<CombinedColumns>("doctors");
  /** The day's zoom, in hours shown at once (CZ-D1) — per person, on this computer. */
  const [hoursShown, setHoursShown] = useHoursShown();
  /** How many hours the day grid can draw at the minimum hour height (CZ-D4). */
  const [capacity, setCapacity] = useState<Capacity | null>(null);
  // A preset (CZ-D5) is not a number of hours; − and + work from the most that fit.
  const chosenHours = isPreset(hoursShown) ? null : hoursShown;
  const drawnHours = effectiveHours(chosenHours, capacity);
  /**
   * The combined day — every shown location's chairs, providers and bookings.
   * Read for the day grid when several locations are shown, and for the
   * filters' lists on the month, which counts on the server.
   */
  const [combinedDay, setCombinedDay] = useState<CombinedCalendar | null>(null);
  /** The clinic's procedure categories: the filter's "Appointment type" and the key. */
  const [categories, setCategories] = useState<ProcedureCategory[]>([]);
  const { user } = useAuth();
  /** A clinician signed in: their combined view opens on their own bookings (CF-D11). */
  const meAsDoctor = user !== null && combined && CLINICAL_ROLES.has(user.role) ? user.id : null;
  const filterState = useCalendarFilters(meAsDoctor);
  const filters = filterState.filters;
  /** Every shown location's day, when several are shown. */
  const shownLocations: CombinedLocation[] = combined ? (combinedDay?.locations ?? []) : [];
  const locationById = (id: string): CombinedLocation | undefined =>
    shownLocations.find((location) => location.branch.id === id);

  /**
   * Which month the grid is showing, as `YYYY-MM`.
   *
   * **Its own state rather than derived from `day`**, because Previous and Next
   * move the month without moving the day — a planner pages through months and
   * only then picks one to open.
   */
  const [monthKey, setMonthKey] = useState(() => today().slice(0, 7));
  useEffect(() => {
    if (knownZone === null) return;
    const guessed = guessedToday.current;
    const actual = todayIn(knownZone);
    guessedToday.current = actual;
    if (guessed === actual) return;
    setDay((current) => (current === guessed ? actual : current));
    setMonthKey((current) => (current === guessed.slice(0, 7) ? actual.slice(0, 7) : current));
  }, [knownZone]);
  const [month, setMonth] = useState<CombinedMonth | null>(null);
  const [chairs, setChairs] = useState<Chair[]>([]);
  /**
   * The clinic's staff, fetched here rather than inside each consumer.
   *
   * **Two things need it** — the rota editor, to offer who can be rostered, and
   * the detail panel, to turn a booking's `providerId` into a name. It used to
   * be fetched inside `ChairRoster`; one owner is one request.
   */
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [roster, setRoster] = useState<DayRoster | null>(null);
  /**
   * The branch's timezone, for turning a typed clock into an instant.
   *
   * **The roster first, because it is already here.** The day grid needs the
   * branch's zone to draw at all, so by the time anybody has a booking form
   * open `roster.frame.timezone` is loaded and authoritative. The hook is the
   * fallback for the window before that — it answers `"UTC"` until `GET
   * /branches` returns, and a booking submitted inside that window would be
   * read as UTC rather than as the branch's clock.
   */
  const hookTimezone = knownZone ?? "UTC";
  const timezone = roster?.frame.timezone ?? hookTimezone;
  const [appointments, setAppointments] = useState<Appointment[] | null>(null);
  /**
   * Events on the chairs' Google calendars that DentaCore did not create
   * (STORY-049b). Its own state, never merged into `appointments`: G14 says an
   * external event is not an appointment and never becomes one, and one array
   * is how that stops being true.
   *
   * Empty is the normal case — most clinics have not connected Google — so a
   * failed read leaves it empty rather than failing the day.
   */
  const [externalBlocks, setExternalBlocks] = useState<readonly ExternalBlock[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /**
   * **The side panel: the waitlist, or what is waiting on WhatsApp, or
   * neither** (decision CS6).
   *
   * The two queues used to sit permanently beside the grid, under a mini month,
   * in a 248px column — a fifth of the width, all day, whether anything was in
   * them or not. They open from two counters in the header now, and the grid
   * takes the width the rest of the time.
   *
   * **The panel pushes the grid rather than covering it**, so a waiting patient
   * can still be dragged onto a free slot (Anthony's QA, item 14). And it opens
   * on the left: the appointment panel already owns the right.
   *
   * Both queues stay mounted while it is shut — hidden, not unrendered — so the
   * counters in the header are real counts before anybody opens anything.
   */
  // **Closed by default** (CZ-D13, reversing CZ-D7's open start): the header's
  // Waitlist and WhatsApp counters open it, in the column the filter panel had.
  const [side, setSide] = useState<"waitlist" | "whatsapp" | null>(null);
  // Only a queue this person may work is drawn; permissions can arrive after the first render.
  const shownSide =
    side === "waitlist" && !can("waitlist.manage")
      ? null
      : side === "whatsapp" && !(hasChatbot && can("appointments.validate"))
        ? null
        : side;
  const [waitlistCount, setWaitlistCount] = useState<number | null>(null);
  const { count: waitingCount } = useWaiting();
  const location = useLocation();

  /**
   * The banner's "waiting on WhatsApp" chip lands on `/calendar#waiting`
   * (HC3). That used to scroll the queue into view; with the queue in the
   * panel, it has to open the panel first — and the panel lives in the day
   * view. Keyed on `location.key` so pressing the chip again while here still
   * opens it after it was closed.
   */
  useEffect(() => {
    if (location.hash !== "#waiting" || !hasChatbot) return;
    setView("day");
    setSide("whatsapp");
  }, [location.hash, location.key, hasChatbot]);

  /**
   * **Home's *Open slots* lands on `/calendar#waitlist`** (FX13): today's day,
   * with the waitlist open beside it — an open slot is worth exactly as much as
   * somebody waiting to fill it.
   */
  useEffect(() => {
    if (location.hash !== "#waitlist") return;
    setView("day");
    setDay(today());
    setSide("waitlist");
  }, [location.hash, location.key]);

  /**
   * An open "move the rest of the plan?" question (CH4), and how to answer it.
   * Held as a resolver so a move can `await` the answer like any other step.
   */
  const [shiftAsk, setShiftAsk] = useState<{
    later: number;
    resolve: (answer: boolean | null) => void;
  } | null>(null);

  /**
   * **An appointment named in the URL opens on arrival** (client's manual QA,
   * item 2).
   *
   * Home's *Patients remaining* card linked to `/calendar` and stopped there,
   * so the desk arrived at the right day and still had to find the booking they
   * had just been counting. Naming it in the query string means the link
   * carries its subject, which also makes it something you can paste to a
   * colleague.
   *
   * Applied once per id rather than on every render, so selecting a different
   * block does not fight the URL that opened the screen.
   */
  const [searchParams] = useSearchParams();
  const wantedId = searchParams.get("appointment");
  /**
   * **What Home's quick ring asked for on that booking** (STORY-221):
   * `ring=step` presses its panel's next step (Complete visit opens its
   * dialog), `ring=pay` its *Record payment*, `ring=move` its *Reschedule*.
   * The panel's own buttons, so Home reaches nothing this screen cannot do.
   */
  const wantedRing = searchParams.get("ring");
  const honoured = useRef<string | null>(null);

  /**
   * **The quick ring's request to the selected booking's panel** (STORY-221),
   * carried out there by the panel's own button. Held until the panel takes it.
   */
  const [ringCommand, setRingCommand] = useState<PanelRingCommand | null>(null);
  const ringNonce = useRef(0);
  const askPanel = (id: string, key: PanelRingKey): void => {
    ringNonce.current += 1;
    setBooking(false);
    setSelectedId(id);
    setRingCommand({ id, key, nonce: ringNonce.current });
  };

  useEffect(() => {
    if (wantedId === null || honoured.current === wantedId) return;
    honoured.current = wantedId;
    setSelectedId(wantedId);
    setBooking(false);
    if (wantedRing === "move") setRescheduling(wantedId);
    else if (wantedRing === "step" || wantedRing === "pay") {
      ringNonce.current += 1;
      setRingCommand({ id: wantedId, key: wantedRing, nonce: ringNonce.current });
    }
  }, [wantedId, wantedRing]);

  /**
   * **Arriving from a patient's file with them already chosen** (item 17).
   *
   * "Next appointment: None booked" was a dead end — the only way on was the
   * calendar, whose first question is *which patient*, asked immediately after
   * leaving theirs. The file now links here with `?patient=<id>`.
   *
   * **A starting point, not a filter.** The booking panel opens with them
   * selected and a *Change* affordance beside them, exactly as starting a
   * treatment plan from a file does (V5) — arriving with the wrong patient is
   * one click to fix rather than a URL to edit.
   *
   * Honoured once, by the same `useRef` discipline as the appointment
   * parameter above: re-applying it on every render would fight anybody who
   * then chose somebody else.
   */
  const wantedPatientId = searchParams.get("patient");
  /**
   * **And the visit to book, from Home's "still to book"** (PV5) —
   * `?patient=<id>&visit=<planId>:<partId>`, the same value the plan picker
   * uses, so the form opens on that visit with its chain proposed under it.
   */
  const wantedVisit = searchParams.get("visit");
  const patientHonoured = useRef<string | null>(null);

  useEffect(() => {
    if (wantedPatientId === null || patientHonoured.current === wantedPatientId) return;

    const controller = new AbortController();

    /**
     * **Marked honoured only once the read has finished**, never before it.
     *
     * It was marked first, and React's development double-run of effects then
     * cancelled the read in the cleanup and skipped the second run as already
     * honoured — the aborted read fell into the `catch`, which opened the
     * panel empty. So arriving with `?patient=` opened the form with nobody
     * chosen, and Home's "still to book" (PV5) opened it without its visit.
     */
    getPatient(wantedPatientId, controller.signal)
      .then((found) => {
        if (controller.signal.aborted) return;
        patientHonoured.current = wantedPatientId;
        setPatient(found);
        if (wantedVisit !== null) setPlanVisitId(wantedVisit);
        setBooking(true);
        setSelectedId(null);
      })
      .catch(() => {
        // Cancelled by the effect's own cleanup: the next run reads again.
        if (controller.signal.aborted) return;
        patientHonoured.current = wantedPatientId;
        // Silent, and the panel simply opens empty. A patient who cannot be
        // read — removed, or in another clinic — is not a reason to refuse to
        // show the calendar.
        setBooking(true);
      });

    return () => controller.abort();
  }, [wantedPatientId, wantedVisit]);
  /**
   * Whether the "New appointment" panel is open — the mockup's right rail,
   * which shows either this or the selected appointment, never both. Stays
   * open across a save rather than closing on it: a front desk booking three
   * patients in a row types the next one straight in, and closing after every
   * save would make them click "New appointment" three times for that.
   */
  const [booking, setBooking] = useState(false);
  /**
   * Whether to draw the booking-in-progress as a shadow block on the grid
   * (client, 2026-08-28: "it should show some sort of shadow appointment while I
   * edit"). Set when the form is opened by dragging a slot — which is the case
   * that has a chair and a time to draw — and cleared once the booking saves or
   * the form is dismissed. The plain "New appointment" button opens the form
   * without one, since there is no slot chosen yet to shadow.
   */
  const [error, setError] = useState<string | null>(null);
  /**
   * Which booking the picker is open on, if any.
   *
   * **The id, not the appointment** — the day is re-read after every move, so
   * holding the row itself would keep a copy that stopped matching the calendar
   * the moment anything changed. It is resolved back to a live row below.
   */
  const [rescheduling, setRescheduling] = useState<string | null>(null);

  /**
   * Bumped whenever the waitlist may have changed underneath the strip.
   *
   * Counts rather than toggles: two changes in a row must both be noticed, and
   * a boolean settles on true and stops meaning anything.
   */
  const [waitlistVersion, setWaitlistVersion] = useState(0);

  const [busy, setBusy] = useState(false);

  /** Whether the admin-only "Edit chairs" dialog is open. */
  const [editingChairs, setEditingChairs] = useState(false);

  const [chairId, setChairId] = useState("");
  const [clock, setClock] = useState("09:00");
  // **A "to" time, not a minute count** (client request, 2026-08-24): asking
  // someone to say "9am, 30 minutes" is asking them to do arithmetic a "9am to
  // 9:30" picker does for them. `durationMinutes` below is the one place that
  // arithmetic still happens, and only for the server, which has always taken
  // a duration rather than an end time.
  const [endClock, setEndClock] = useState("09:30");
  const [summary, setSummary] = useState("");
  /**
   * Which catalogue row the picker chose, or null when the reason was typed.
   *
   * **Typing clears it** (see the field below). The text is the only thing on
   * screen, so a hidden link that disagrees with the visible label is exactly
   * how a prep list ends up confidently naming the wrong tray.
   */
  const [procedureCodeId, setProcedureCodeId] = useState<string | null>(null);
  /**
   * Whether the free-text "For" box is showing.
   *
   * Only an explicit "Something else" opens it, so the ordinary path — picking
   * a procedure from the catalogue — never leaves a spare field on the form.
   * With no catalogue at all the box is the only control there is and this is
   * not consulted.
   */
  const [describing, setDescribing] = useState(false);

  /**
   * The treatment-plan visit this booking is for, as `planId:partId`.
   *
   * **Booking is where this belongs** (client, 2026-09-06). Decision D6 added
   * the link and put it on the appointment *panel* — after the fact — so the
   * only way to say a booking was part of a plan was to make it and then edit
   * it. His report was blunt: *"i am still not able to add a treatment plan to
   * the book a new appointment for a patient."*
   */
  const [planVisitId, setPlanVisitId] = useState("");

  /**
   * Set when the booking landed but attaching it to a plan did not.
   *
   * Keeps the dialog open so the message above the buttons is readable —
   * the booking is real either way, and closing would hide the half that
   * failed.
   */
  const [linkFailed, setLinkFailed] = useState(false);
  const [patientPlans, setPatientPlans] = useState<TreatmentPlan[]>([]);

  /** Whether the remaining visits are booked too. On by default (CH2). */
  const [chainInclude, setChainInclude] = useState(true);
  /** The tooth a per-tooth multi-visit procedure is for (TP12), asked once. */
  const [chainTeeth, setChainTeeth] = useState<number[]>([]);
  /**
   * Visits of a chain the server refused (CH10). **Shown, never swallowed** —
   * each stays unbooked and waits to be booked (CH9), and the front desk has
   * to know which.
   */
  const [chainRefused, setChainRefused] = useState<RefusedVisit[]>([]);
  const [isBlock, setIsBlock] = useState(false);
  /**
   * **Where the booking goes, with several locations shown** (CF-D15): asked
   * first, empty by default and required — nothing is chosen for you. Its
   * chairs and doctors are the only ones offered, and the booking is written
   * to it. Unused with one location, which books where it always did.
   */
  const [bookingBranchId, setBookingBranchId] = useState("");
  /** Where the form's requests go: the chosen location, or null for the active one. */
  const bookingAt: string | null = combined && bookingBranchId !== "" ? bookingBranchId : null;
  const bookingLocation = combined ? locationById(bookingBranchId) : undefined;
  /** The clock a typed time means: the booking's own location's (CF-D14). */
  const formTimezone = bookingLocation?.branch.timezone ?? timezone;
  /**
   * Whether the New patient dialog is open over the booking form (FX15) — for
   * somebody calling to book who has no file yet. Only offered to whoever may
   * create patients.
   */
  const [addingPatient, setAddingPatient] = useState(false);
  /**
   * Why the chair is being held, for a block (STORY-046d).
   *
   * **Empty is a real answer**, not a missing one: a practice that has not
   * filled its list has nothing to pick, and a block with no reason still holds
   * the chair and still reaches Google saying so.
   */
  const [blockReasonId, setBlockReasonId] = useState("");
  const [blockReasons, setBlockReasons] = useState<readonly BlockReason[]>([]);

  /**
   * Who is working in this branch on the day being viewed.
   *
   * **Re-read whenever the day changes**, because a rota is a fact about a day
   * rather than about a branch — somebody rostered Monday is not rostered
   * Tuesday, and a stale list would offer them.
   */
  const [availability, setAvailability] = useState<ProviderAvailability[]>([]);
  const [providerId, setProviderId] = useState("");

  /**
   * Is the chosen provider already booked over this time, in another chair?
   *
   * ── The other half of §4.3 (QA #13) ───────────────────────────────────────
   *
   * The working plan settled that a provider in two places at once "raises a
   * **soft warning**, never a block". The block half was correctly built —
   * `ConflictService` deliberately says nothing about providers, and there is
   * no matching database constraint. The warning half was never built at all:
   * the server defers it to the UI in a comment, and the UI never picked it up,
   * so the rule shipped as silence.
   *
   * **Derived, not fetched.** The day's appointments are already loaded to draw
   * the grid, so the answer is here — and computing it from the same list the
   * user is looking at is what keeps the warning and the screen agreeing.
   *
   * **A warning, so it is deliberately loose.** It names the overlap and does
   * nothing else: no disabled button, no confirmation step. One clinician
   * starting an extraction while another finishes next door is ordinary
   * practice, and the person booking knows which case this is.
   */
  /**
   * Every clinician who could be treating them — **this location's, and only
   * this location's** (FX18).
   *
   * `/appointments/providers` lists every member of the branch, rostered or
   * not, available first (STORY-040). Item 14 once topped it up with the whole
   * clinic's clinical staff so a practice with no rota still had names to
   * pick, which also offered dentists who never work here. Membership is the
   * line now: somebody added to this location is offered whether or not a rota
   * exists, and somebody who is not, is not.
   *
   * The server still accepts an unrostered provider (P3 Q16), so a member who
   * is off today stays choosable — labelled with why (FX19), never hidden.
   */
  const providerChoices = useMemo(
    () =>
      combined
        ? (bookingLocation?.providers ?? []).map((person) => ({
            userId: person.userId,
            name: person.name,
            role: person.role,
            away: null,
          }))
        : availability.filter((person) => CLINICAL_ROLES.has(person.role)),
    [availability, combined, bookingLocation],
  );

  /**
   * **It never fired, and FX24 is why it does now.** The other booking's times
   * were read with `getUTCHours()` and compared with the clock typed into the
   * form, which is the branch's — so in any zone but UTC the two were hours
   * apart and an 08:00–09:00 never met an 08:30 booking. Both sides are now on
   * the branch's clock, through the same helper the form books with.
   */
  const providerClash = useMemo(() => {
    // With several locations shown the server refuses a doctor booked elsewhere (CF-D10).
    if (combined || providerId === "" || isBlock || appointments === null) return null;

    const startMinutes = minutesOfClock(clock);
    const endMinutes = minutesOfClock(endClock);
    if (endMinutes <= startMinutes) return null;

    for (const appointment of appointments) {
      if (appointment.providerId !== providerId) continue;
      // Cancelled and missed visits hold nobody's time.
      if (!blocksTheChair(appointment)) continue;

      const from = wallClockOfInstant(new Date(appointment.startsAt), timezone);
      const to = wallClockOfInstant(new Date(appointment.endsAt), timezone);
      if (from.day !== day) continue;

      const otherStart = minutesOfClock(from.clock);
      const otherEnd = to.day === day ? minutesOfClock(to.clock) : 24 * 60;
      if (otherStart < endMinutes && otherEnd > startMinutes) {
        return { appointment, from: from.clock, to: to.clock };
      }
    }
    return null;
  }, [providerId, isBlock, appointments, clock, endClock, day, timezone]);

  const [patientQuery, setPatientQuery] = useState("");
  const [matches, setMatches] = useState<Patient[]>([]);
  const [patient, setPatient] = useState<Patient | null>(null);
  /** The others in this family visit, in order (FV-D1). */
  const [family, setFamily] = useState<FamilyMember[]>([]);
  /** The first patient's procedures after the one in *For* (FV-D6). */
  const [moreCodes, setMoreCodes] = useState<string[]>([]);
  /** Whether *New patient* is for the visit's first patient or one more of them. */
  const [addingFamily, setAddingFamily] = useState(false);

  /**
   * The waitlist entry currently being dragged onto the grid, if any.
   *
   * Lives here rather than in the strip because the *drop* happens in the grid,
   * and these are siblings — the page is the nearest thing that can see both.
   * `dataTransfer` carries only the entry id, deliberately, so this is where
   * the rest of the entry comes from. See `WAITLIST_DRAG_TYPE`.
   */
  const [dragging, setDragging] = useState<WaitlistEntry | null>(null);

  /**
   * The chosen patient's plans, re-read whenever the patient changes.
   *
   * A failed read is silent and leaves the picker absent: a plan is optional on
   * a booking, and an unreachable plans endpoint must not stop a receptionist
   * booking somebody in.
   */
  useEffect(() => {
    if (patient === null) {
      setPatientPlans([]);
      setPlanVisitId("");
      setLinkFailed(false);
      return;
    }

    const controller = new AbortController();
    listPlans(patient.id, controller.signal)
      .then(setPatientPlans)
      .catch(() => setPatientPlans([]));

    return () => controller.abort();
  }, [patient]);

  /**
   * Each live plan's next unfinished visit — the only ones the plan's own order
   * rule (TP8) accepts, so the only ones worth offering.
   */
  const planVisitOptions = useMemo(() => {
    const rows: { value: string; label: string; keywords: string }[] = [];

    for (const plan of patientPlans) {
      /**
       * **Open means not finished and not abandoned** (TP29, TP31).
       *
       * This used to read `status !== "IN_PROGRESS"`, against a column nothing
       * ever advanced — so it matched every plan ever created, and a patient
       * whose crown was finished last month was still offered it here.
       */
      if (!isPlanOpen(plan)) continue;

      /**
       * **The first visit with no live booking that is not yet done** — which
       * now includes one whose booking fell through (PV5).
       *
       * It used to require every step still `PLANNED`, so a visit whose
       * appointment was cancelled or missed — its steps stay `SCHEDULED` — was
       * never offered, on the grounds that rebooking was the plans screen's
       * job. That screen is gone (PV6), and Home's "still to book" sends a
       * stalled visit here. `linkPart` refuses only a visit that still has a
       * *live* appointment, so offering one whose booking is dead is safe;
       * offering one with a live booking is still not, and `booking` is null
       * exactly when there is no live one.
       */
      const index = plan.parts.findIndex(
        (part) => part.items.length > 0 && part.completedAt === null && part.booking === null,
      );
      const part = index === -1 ? undefined : plan.parts[index];
      if (part === undefined) continue;

      rows.push({
        value: `${plan.id}:${part.id}`,
        label: `${plan.title ?? "Treatment plan"} — visit ${String(index + 1)}`,
        keywords: part.items.map((item) => item.label ?? "").join(" "),
      });
    }

    // **Courses already started** (PV3) and old plans still in progress. A new
    // course starts from a multi-visit procedure in "For" instead.
    return rows;
  }, [patientPlans]);

  /**
   * The branch's procedure catalogue, for the booking form's "For" field
   * (client's manual QA, item 25: "the procedures done should be listed in a
   * dropdown").
   *
   * It was a free-text box with a placeholder of examples, so what a booking
   * was *for* was whatever somebody typed — the same procedure spelled three
   * ways across three receptionists, and none of them matching the catalogue
   * the invoice is priced from.
   *
   * **A failure here is not an error worth showing**, the same call the billing
   * tab makes: the catalogue ships empty (ADR-0008), and a branch that has not
   * built one yet still has to be able to take a booking.
   */
  const [catalogue, setCatalogue] = useState<ProcedureCode[]>([]);
  /** The selected booking's own location's catalogue, on the combined calendar. */
  const [panelCodes, setPanelCodes] = useState<ProcedureCode[] | null>(null);

  /**
   * What the chosen plan has left to book after this visit (CH2): the visits
   * straight after the one being booked, **up to the first that already has a
   * date or is done** — past that point the plan is already scheduled, and a
   * proposal would only collide with it (CH8).
   */
  const chainSource = useMemo((): {
    /** Set when the course is created at Book (PV3); null for an existing plan. */
    procedureCodeId: string | null;
    planId: string | null;
    firstPartId: string | null;
    needsTooth: boolean;
    visits: ChainVisit[];
  } | null => {
    /**
     * **A multi-visit procedure books its chain** (PV3) — the procedure chosen
     * in "For", with a patient, and no existing plan visit picked. Its visits
     * after the first are proposed from their own gaps and lengths; the course
     * itself is only created when Book is pressed, so a form abandoned halfway
     * leaves nothing behind.
     */
    if (planVisitId === "") {
      const code = catalogue.find((item) => item.id === procedureCodeId);
      if (code === undefined || code.visits.length < 2 || patient === null) return null;
      return {
        procedureCodeId: code.id,
        planId: null,
        firstPartId: null,
        needsTooth: code.scope === "PER_TOOTH",
        visits: code.visits.slice(1).map((visit, at) => ({
          partId: null,
          visitNumber: at + 2,
          title: visit.title,
          gapDays: visit.gapDays,
          durationMinutes: visit.durationMinutes,
        })),
      };
    }

    const [planId, partId] = planVisitId.split(":");
    const plan = patientPlans.find((row) => row.id === planId);
    const index = plan?.parts.findIndex((part) => part.id === partId) ?? -1;
    if (plan === undefined || planId === undefined || partId === undefined || index === -1) {
      return null;
    }

    const visits: ChainVisit[] = [];
    for (const [at, part] of plan.parts.entries()) {
      if (at <= index) continue;
      if (part.booking !== null || part.completedAt !== null || part.items.length === 0) break;
      visits.push({
        partId: part.id,
        visitNumber: at + 1,
        title: part.title,
        gapDays: part.gapDays,
      });
    }
    return { procedureCodeId: null, planId, firstPartId: partId, needsTooth: false, visits };
  }, [planVisitId, patientPlans, catalogue, procedureCodeId, patient]);

  const chainVisits = useMemo(() => chainSource?.visits ?? NO_CHAIN, [chainSource]);

  /**
   * **A one-visit procedure done on a tooth** (BF3) — the tooth is asked, on the
   * chart, and **required since QA4-D4** (2026-10-18), reversing BF3's
   * "optional": a clinic that marked a check-up per-tooth changes it to whole
   * mouth instead. Not for a visit of an existing plan, whose tooth is
   * already on its step, nor a multi-visit procedure, which asks for its own.
   */
  const oneVisitCode =
    planVisitId === "" && chainSource === null
      ? catalogue.find((item) => item.id === procedureCodeId)
      : undefined;
  const asksToothOnce =
    !isBlock &&
    patient !== null &&
    oneVisitCode !== undefined &&
    oneVisitCode.scope === "PER_TOOTH";

  /**
   * **Empty the booking form** (client's manual QA, item 27).
   *
   * Cancelling left every field as it was, so the next booking began pre-filled
   * with the last one's patient, procedure, provider and repeat count — and the
   * client found their abandoned entry waiting for them. Saving cleared three
   * of the eight, which is how the repeat count and the provider leaked into
   * the following booking.
   *
   * The chair and the clock are deliberately **not** reset: they are set by the
   * grid slot you clicked, and clearing them would undo the thing that opened
   * the form. Everything the form itself asks for is emptied.
   */
  const clearBookingForm = (): void => {
    setSummary("");
    setProcedureCodeId(null);
    setDescribing(false);
    setPatient(null);
    setFamily([]);
    setMoreCodes([]);
    setAddingFamily(false);
    setPatientQuery("");
    setMatches([]);
    setProviderId("");
    setPlanVisitId("");
    setChainTeeth([]);
    setChainInclude(true);
    setIsBlock(false);
    setBlockReasonId("");
    setAddingPatient(false);
  };

  /**
   * Reusable rather than only an effect, because retiring or adding a chair
   * (`EditChairs`) has to re-read the same list the booking form and the
   * grid both already trust — one loader, called from two places, is what
   * keeps them from disagreeing about which chairs currently exist.
   */
  const reloadChairs = useCallback(
    (signal?: AbortSignal): Promise<void> =>
      listChairs(signal)
        .then((rows) => {
          setChairs(rows);
          // Only ever defaults to an active chair — a retired one dropped
          // from the booking form's own options a moment later would leave
          // the select pointing at nothing selectable.
          setChairId((current) =>
            current !== "" && rows.some((row) => row.id === current && row.isActive)
              ? current
              : (rows.find((row) => row.isActive)?.id ?? ""),
          );
        })
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          // **Not swallowed.** An empty chair list and a refused request look
          // identical on screen, and the second is a fault worth seeing.
          setChairs([]);
          setError(cause instanceof Error ? cause.message : "Could not read this branch's chairs.");
        }),
    [],
  );

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();

    void reloadChairs(controller.signal);

    return () => controller.abort();
  }, [branchId, reloadChairs]);

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();

    // Per branch, because each owns its catalogue outright (ADR-0008). Live
    // codes only — a retired procedure is one the branch stopped offering, and
    // offering it in a booking form is how it comes back by accident.
    atLocation(bookingAt, () => listProcedureCodes(false, controller.signal))
      .then(setCatalogue)
      .catch(() => {
        setCatalogue([]);
      });
    // A booking at another location holds the chair for that location's reasons.
    if (bookingAt !== null) {
      atLocation(bookingAt, () => listBlockReasons(false, controller.signal))
        .then(setBlockReasons)
        .catch(() => setBlockReasons([]));
    }

    return () => controller.abort();
  }, [branchId, bookingAt]);

  useEffect(() => {
    const controller = new AbortController();

    // Clinic-scoped, so it does not re-run on a branch change. A failure is
    // survivable: the rota editor offers nobody and the panel says the provider
    // is unnamed, which beats the whole screen failing.
    listStaff(controller.signal)
      .then(setStaff)
      .catch(() => setStaff([]));

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();

    listProviderAvailability(day, controller.signal)
      .then(setAvailability)
      // Survivable: the picker falls back to offering nobody rather than the
      // whole screen failing, and the field is optional on a booking anyway.
      .catch(() => setAvailability([]));

    return () => controller.abort();
  }, [branchId, day]);

  useEffect(() => {
    if (branchId === null || view !== "week" || shownIds.length === 0) return;
    const controller = new AbortController();

    setWeek(null);
    // **From the Sunday of the day being viewed**, so switching between views
    // keeps the reader where they were rather than jumping back to today.
    getCombined(weekStartOf(day), 7, shownIds, controller.signal)
      .then(setWeek)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setWeek(null);
        setError(cause instanceof Error ? cause.message : "Could not read that week.");
      });

    return () => controller.abort();
    // `shownKey` stands for `shownIds`, which is a new array every render.
  }, [branchId, day, view, shownKey]);

  /** A month is counted on the server, with the filters applied (CF-D13). */
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (branchId === null || view !== "month" || shownIds.length === 0) return;
    const controller = new AbortController();

    getCombinedMonth(monthKey, shownIds, filters, controller.signal)
      .then(setMonth)
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setMonth(null);
        setError(cause instanceof Error ? cause.message : "Could not read that month.");
      });

    return () => controller.abort();
    // `filtersKey` and `shownKey` stand for the objects they are made from.
  }, [branchId, monthKey, view, shownKey, filtersKey]);

  /**
   * **The combined day** — the grid's own read with several locations shown,
   * and the filters' lists for the month view. A single-location day keeps
   * reading what it always did, so dragging, Google blocks and the waitlist
   * drop work there exactly as before.
   */
  const reloadCombinedDay = useCallback(
    (signal?: AbortSignal): Promise<void> =>
      getCombined(day, 1, shownIds, signal)
        // A family visit drawn as one card (FV-D15), at every location.
        .then((read) =>
          setCombinedDay({
            ...read,
            locations: read.locations.map((location) => ({
              ...location,
              appointments: groupVisits(location.appointments),
            })),
          }),
        )
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          setCombinedDay(null);
          if (combined)
            setError(cause instanceof Error ? cause.message : "Could not read that day.");
        }),
    // `shownKey` stands for `shownIds`.
    [day, shownKey, combined],
  );

  useEffect(() => {
    if (branchId === null || shownIds.length === 0) return;
    if (!combined && view !== "month") return;
    const controller = new AbortController();
    if (combined) setCombinedDay(null);
    void reloadCombinedDay(controller.signal);
    return () => controller.abort();
  }, [branchId, combined, view, reloadCombinedDay]);

  useEffect(() => {
    const controller = new AbortController();
    listProcedureCategories(controller.signal)
      .then(setCategories)
      .catch(() => setCategories([]));
    return () => controller.abort();
  }, []);

  const reloadRoster = useCallback(
    (signal?: AbortSignal): Promise<void> =>
      getRoster(day, signal)
        .then(setRoster)
        .catch((cause: unknown) => {
          if (signal?.aborted === true) return;
          // The grid needs the rota for its columns **and** for the day's
          // bounds, so a failure here is an empty screen rather than a missing
          // strip. Said out loud for that reason.
          setError(cause instanceof Error ? cause.message : "Could not read the schedule.");
        }),
    [day],
  );

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();

    setRoster(null);
    void reloadRoster(controller.signal);

    return () => controller.abort();
  }, [branchId, reloadRoster]);

  useEffect(() => {
    if (branchId === null) return;
    const controller = new AbortController();

    setAppointments(null);
    listAppointments({ from: day }, controller.signal)
      .then((rows) => setAppointments(groupVisits(rows)))
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setAppointments([]);
        setError(cause instanceof Error ? cause.message : "Could not read that day.");
      });

    /**
     * Read alongside the day rather than as part of it, and **never allowed to
     * fail the day**. A clinic with no Google pairing gets an empty array from
     * a healthy server; one whose Google is broken should still see its own
     * bookings rather than an error where the calendar was. The blocks are an
     * addition to the picture, so their absence degrades to the picture without
     * them.
     */
    setExternalBlocks([]);
    listExternalBlocks({ from: day }, controller.signal)
      .then(setExternalBlocks)
      .catch(() => {
        if (controller.signal.aborted) return;
        setExternalBlocks([]);
      });

    /**
     * The reasons a chair can be held (STORY-046d). **Active only** — the
     * picker offers what can be chosen now, and a retired reason stays on the
     * blocks already held under it (C3's shape).
     *
     * Never allowed to fail the day: a practice with no reasons yet books
     * exactly as it did before, and the block reaches Google saying only that
     * the chair is held.
     */
    listBlockReasons(false, controller.signal)
      .then(setBlockReasons)
      .catch(() => {
        if (controller.signal.aborted) return;
        setBlockReasons([]);
      });

    return () => controller.abort();
  }, [day, branchId]);

  // A shadow booking belongs to the day it was drawn on. Changing day (or
  // branch) leaves its chair and time meaningless, so drop it rather than
  // draw it somewhere it was never placed.
  useEffect(() => {}, [day, branchId]);

  /** Patient search, debounced, and only once there is enough to search on. */
  useEffect(() => {
    if (isBlock || patientQuery.trim().length < 2) {
      setMatches([]);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      // **The three best matches** (FX23): the search ranks by how well each
      // file matches, so twenty Georges show the three closest, not all twenty.
      searchPatients({ q: patientQuery, limit: 3 }, controller.signal)
        .then((page) => setMatches(page.patients))
        .catch(() => setMatches([]));
    }, 250);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [patientQuery, isBlock]);

  const reload = (): Promise<void> =>
    combined && view === "day"
      ? reloadCombinedDay()
      : listAppointments({ from: day }).then((rows) => setAppointments(groupVisits(rows)));

  // **Another receptionist's booking appears without a refresh** (OQ-D19).
  useLiveRefresh(["appointments", "booking_requests", "waitlist_entries"], () => {
    void reload().catch(() => undefined);
  });

  // The quick ring moves a visit on from outside this screen (QR6).
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    const onChanged = () => void reloadRef.current();
    window.addEventListener(APPOINTMENTS_CHANGED, onChanged);
    return () => window.removeEventListener(APPOINTMENTS_CHANGED, onChanged);
  }, []);

  // Negative when "to" is dragged before "from" — `canBook` below refuses it
  // rather than sending a duration the server would refuse anyway.
  const durationMinutes = minutesOfClock(endClock) - minutesOfClock(clock);

  /** How long these procedures take together — a family member's slot (FV-D2). */
  const lengthOfCodes = (ids: readonly string[]): number =>
    ids.reduce(
      (sum, id) => sum + (catalogue.find((code) => code.id === id)?.defaultDurationMinutes ?? 0),
      0,
    );
  /**
   * **One-visit procedures only, for an added child or a further procedure**
   * (FV-D18): a multi-visit one books a chain, which belongs to one booking.
   */
  const oneVisitCatalogue = catalogue.filter((code) => code.visits.length < 2);

  /**
   * The remaining visits, proposed from this booking's chair, day, time and
   * length (CH2, CH3). Not for a block, and not for a weekly repeat — a repeat
   * is a series of the *same* booking, and a chain is a plan's different visits.
   */
  const chain = useChainRows({
    visits: chainVisits,
    chairId,
    patientId: patient?.id ?? null,
    day,
    clock,
    durationMinutes,
    timezone: formTimezone,
    enabled: !isBlock,
    branchId: bookingAt,
  });

  const book = (): void => {
    // **A visit is booked into exactly one location** (CF-D15), and with several
    // shown nothing chooses it for you.
    if (combined && bookingBranchId === "") {
      setError("Choose the location: a visit is booked into exactly one.");
      return;
    }
    setBusy(true);
    setError(null);
    setChainRefused([]);

    /**
     * **Each child's slot is their procedures' length** (FV-D2, FV-D12), and the
     * first patient has what is left of *From–To* — so the visit fills exactly
     * the time on the form, one after another.
     */
    const party = family.map((member) => ({
      patientId: member.patient.id,
      durationMinutes: lengthOfCodes(member.procedureCodeIds),
      procedureCodeIds: member.procedureCodeIds,
    }));
    const firstMinutes =
      durationMinutes - party.reduce((sum, member) => sum + member.durationMinutes, 0);
    if (!isBlock && party.length > 0 && firstMinutes < 5) {
      setBusy(false);
      setError(
        `From–To is too short for everyone: the others need ${String(
          durationMinutes - firstMinutes,
        )} minutes. Move To later.`,
      );
      return;
    }

    const shared = {
      chairId,
      /**
       * **The branch's clock, not the browser's** (P2 Q8) — and this line said
       * the opposite until 2026-09-12.
       *
       * It read `new Date(\`${day}T${clock}:00\`)`, a date string with no zone,
       * which every engine parses as *the browser's* local time. The comment
       * that stood here argued that was correct and cited P2 Q8 for it; P2 Q8
       * decided the other way — *"the viewer's browser was rejected outright —
       * it lets the same visit show two dates for two staff."*
       *
       * Everything else on this screen already drew in the branch's zone:
       * `getUTCHours()` on the day grid, `timeZone` on the month, the week and
       * the mini-month. So the two agreed only while the person booking
       * happened to sit in the branch's zone, which is why it survived — and
       * why the test suite, which runs in UTC against a UTC branch, could not
       * see it.
       *
       * Measured on a GMT+3 machine against a UTC branch: typing 16:00 booked
       * 13:00, the form reported success, and the grid drew the block three
       * hours earlier than the receptionist had just typed.
       *
       * **`instantOfWallClock` is the same helper the plan's booking form has
       * used since the timezone fix landed** (`zoned-time.ts`); this was the
       * other caller, and it was missed.
       */
      startsAt: instantOfWallClock(day, clock, formTimezone).toISOString(),
      durationMinutes: party.length > 0 ? firstMinutes : durationMinutes,
      ...(isBlock || patient === null ? {} : { patientId: patient.id }),
      ...(isBlock || providerId === "" ? {} : { providerId }),
      ...(summary.trim() === "" ? {} : { procedureSummary: summary.trim() }),
      ...(procedureCodeId === null ? {} : { procedureCodeId }),
      // The booking's teeth (BF3, BF6): red on the chart until it is done.
      ...(asksToothOnce && chainTeeth.length > 0 ? { fdiNumbers: chainTeeth } : {}),
      // Only on a block, and only when one was chosen. The server ignores it
      // alongside a patient anyway, but sending it would be saying something
      // untrue about the booking.
      ...(isBlock && blockReasonId !== "" ? { blockReasonId } : {}),
      // The first patient's further procedures (FV-D6), only after a first.
      ...(isBlock || procedureCodeId === null || moreCodes.length === 0
        ? {}
        : { moreProcedureCodeIds: moreCodes }),
      // The rest of the family, in order, each with their own length (FV-D1).
      ...(isBlock || party.length === 0 ? {} : { party }),
    };

    /**
     * **Always one booking** — the weekly *Repeat* went on 2026-09-24 (Anthony:
     * "it seems useless"). A course of treatment is a multi-visit procedure now,
     * which books its own chain (PV3); the series endpoint stays on the server.
     */
    const saving: Promise<readonly RefusedOccurrence[]> = (async (): Promise<
      readonly RefusedOccurrence[]
    > => {
      let planId: string | null = chainSource?.planId ?? null;
      let firstPartId: string | null = chainSource?.firstPartId ?? null;
      let partIds: string[] = [];

      /**
       * **A multi-visit procedure's course and its first visit, in one
       * request** (PV3, PP-D19, audit F-17). Started, booked and linked as
       * three calls, a refused slot left an empty course behind and a double
       * click made two; the server now writes both or neither.
       */
      if (chainSource?.procedureCodeId != null && patient !== null) {
        const courseCodeId = chainSource.procedureCodeId;
        const course = await atLocation(bookingAt, () =>
          startCourse({
            patientId: patient.id,
            procedureCodeId: courseCodeId,
            // One set of visits for every tooth chosen (BF6).
            ...(chainSource.needsTooth ? { fdiNumbers: chainTeeth } : {}),
            firstVisit: shared,
          }),
        );
        planId = course.id;
        partIds = course.parts.map((part) => part.id);
        firstPartId = null;
      }

      const created =
        planId !== null && firstPartId === null
          ? null
          : await atLocation(bookingAt, () => createAppointment(shared));

      /**
       * **The plan is attached to the booking that was just made.**
       *
       * A two-step rather than a field on `POST /appointments`: the link
       * lives on the plan's steps (decision D6), and one route owning
       * both would put the plan's rules inside the booking path, where a
       * refusal would read as the *slot* being unavailable.
       *
       * **A failed link does not fail the booking.** The appointment is
       * real and the patient is expected; the plan can be attached from
       * the panel afterwards, and the message says which happened.
       */
      if (planId !== null && firstPartId !== null && created !== null) {
        try {
          await atLocation(bookingAt, () => linkPart(planId, firstPartId, created.id));
        } catch (cause) {
          /**
           * **The dialog stays open, or nobody ever sees this.**
           *
           * The booking succeeded and the link did not, which is a
           * half-done outcome somebody has to know about — and the happy
           * path closes the dialog, taking the message with it.
           */
          setError(
            `The appointment was booked, but it could not be attached to that plan: ${
              cause instanceof Error ? cause.message : "unknown reason"
            }`,
          );
          setLinkFailed(true);
          return [{ startsAt: created.startsAt, reason: "LINK_FAILED", message: "" }];
        }
      }

      /**
       * **Then the rest of the plan** (CH2), each visit as the person left
       * it. A visit with no date is simply not sent: it stays unbooked and
       * waits to be booked (CH9). Refusals come back as data (CH10) and
       * keep the dialog open, so nobody believes in a date they do not have.
       */
      const dated = chainInclude
        ? chain.rows.filter((row) => row.day !== "" && row.clock !== "")
        : [];
      if (planId === null || dated.length === 0) return [];

      const visits = dated.flatMap((row) => {
        // A new course's visits exist only now, found by their number.
        const partId = row.partId ?? partIds[row.visitNumber - 1];
        if (partId === undefined) return [];
        return [
          {
            partId,
            chairId,
            startsAt: instantOfWallClock(row.day, row.clock, formTimezone).toISOString(),
            // The visit's own length when the procedure gives one (PV1).
            durationMinutes: row.durationMinutes ?? durationMinutes,
            ...(providerId === "" ? {} : { providerId }),
          },
        ];
      });

      let refusedVisits: RefusedVisit[];
      try {
        refusedVisits = (await atLocation(bookingAt, () => bookChain(planId, { visits }))).refused;
      } catch (cause) {
        const listed =
          cause instanceof ApiError
            ? (cause.details?.refused as RefusedVisit[] | undefined)
            : undefined;
        refusedVisits =
          listed ??
          visits.map((visit) => ({
            partId: visit.partId,
            startsAt: visit.startsAt,
            reason: null,
            message: cause instanceof Error ? cause.message : "Not booked.",
          }));
      }
      setChainRefused(refusedVisits);
      return refusedVisits.map((row) => ({
        startsAt: row.startsAt,
        reason: row.reason ?? "REFUSED",
        message: row.message,
      }));
    })();

    saving
      .then((turnedAway) => reload().then(() => turnedAway))
      .then((turnedAway) => {
        clearBookingForm();
        /**
         * **The dialog closes on a booking that fully succeeded** (T6).
         *
         * The rail deliberately stayed open, on the reasoning that a
         * receptionist booking three appointments in a row should not press
         * *New appointment* three times — sound while the form sat *beside* the
         * grid and the new block appeared next to it. A dialog covers the grid,
         * so staying open hides the very thing that was just created, and the
         * reasoning inverts: re-opening is one click, and seeing what you
         * booked is the point of booking it.
         *
         * **Except when part of a course was turned away**, which is a 201 with
         * a list attached (P3 Q15: book what fits and report the rest). Closing
         * on that would hide the report the whole decision exists to produce —
         * the patient would believe they have appointments they do not. Caught
         * by `recurring.spec.ts`, which is the test P3 Q15 says this story most
         * needs.
         */
        if (turnedAway.length === 0 && !linkFailed) setBooking(false);
      })
      .catch((cause: unknown) => {
        // The server's sentence, verbatim — it names the branch's own hours and
        // says what got in the way.
        setError(cause instanceof Error ? cause.message : "That booking did not save.");
      })
      .finally(() => setBusy(false));
  };

  /**
   * Commit a drag.
   *
   * **The server is asked every time**, even though the grid already refused
   * the obviously-illegal drops: the day on screen is a snapshot, and between
   * reading it and releasing the pointer somebody else may have taken the slot.
   * A refusal shows the server's own sentence and the day is re-read, which
   * puts the block back where it really is rather than where this browser
   * thought it was.
   */
  const move = (appointment: Appointment, startsAt: Date, chairId: string): void => {
    setError(null);

    moveKeepingChain(appointment, startsAt.toISOString(), chairId)
      .then(() => reload())
      .catch((cause: unknown) => {
        const said = cause instanceof Error ? cause.message : "That move did not save.";
        setError(said);
        // The booking form is closed during a drag, so it is said here too.
        say(said);
        return reload();
      });
  };

  /**
   * **A short notice in the toolbar** — a refused drop (OQ-D13) or a move the
   * server turned down. `error` is drawn inside the booking form only, which is
   * closed while somebody drags; this is where they are looking.
   */
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef(0);
  const say = (message: string): void => {
    setNotice(message);
    window.clearTimeout(noticeTimer.current);
    noticeTimer.current = window.setTimeout(() => setNotice(null), 6000);
  };

  /**
   * **Every move goes through here** — a drag and the reschedule picker alike —
   * so a chained visit always asks before it moves (CH4). *Yes* shifts the rest
   * of the plan with it; *Only this one* is the ordinary move, which the server
   * refuses if it would pass the next visit (CH8); closing moves nothing.
   */
  const moveKeepingChain = async (
    appointment: Appointment,
    startsAt: string,
    chairId?: string,
  ): Promise<"moved" | "cancelled"> => {
    /**
     * **Known from the day already loaded** (`chain` on the appointment), so an
     * ordinary move is sent the instant it is dropped. It was a request per
     * drop at first, which delayed every move on the calendar — plan or not —
     * and a page left in that moment took the move with it.
     */
    const chain = appointment.chain ?? null;
    // Every write goes to the booking's own location (CF-D15).
    const at = combined ? appointment.branchId : null;

    if (chain !== null && chain.laterBooked > 0) {
      const answer = await new Promise<boolean | null>((resolve) => {
        setShiftAsk({ later: chain.laterBooked, resolve });
      });
      setShiftAsk(null);
      if (answer === null) return "cancelled";

      if (answer) {
        const result = await atLocation(at, () =>
          shiftChain(chain.planId, {
            appointmentId: appointment.id,
            startsAt,
            ...(chairId === undefined ? {} : { chairId }),
          }),
        );
        // **Said, never swallowed** (CH10): a visit that stayed put is a date
        // the patient may believe has moved.
        if (result.refused.length > 0) {
          setError(
            `Moved, but ${
              result.refused.length === 1
                ? "one later visit"
                : `${String(result.refused.length)} later visits`
            } could not move and stayed where they were: ${result.refused
              .map((row) => row.message)
              .join(" ")}`,
          );
        }
        return "moved";
      }
    }

    await atLocation(at, () =>
      moveAppointment(appointment.id, {
        startsAt,
        ...(chairId === undefined ? {} : { chairId }),
      }),
    );
    return "moved";
  };

  /**
   * **Open another visit of a chain** (CH7): its day on the grid, and its
   * panel. The day is read on the branch's clock, so a 23:30 UTC visit in a
   * zone ahead of UTC lands on the day the clinic calls it.
   */
  const openAppointment = (appointmentId: string, startsAt: string): void => {
    setView("day");
    setDay(wallClockOfInstant(new Date(startsAt), timezone).day);
    setSelectedId(appointmentId);
  };

  /**
   * The selected booking and the one being rescheduled, resolved from the day
   * that is actually on screen. Both go null when the day changes underneath
   * them, which closes the dialog rather than leaving it open over a booking
   * nobody is looking at any more.
   */
  const combinedAppointments = shownLocations.flatMap((location) => location.appointments);
  const onScreen = combined && view === "day" ? combinedAppointments : (appointments ?? []);
  const selected = onScreen.find((row) => row.id === selectedId) ?? null;
  const rescheduled = onScreen.find((row) => row.id === rescheduling) ?? null;
  /** The selected booking's location, when several are shown — its panel acts there. */
  const selectedLocation =
    combined && selected !== null ? locationById(selected.branchId) : undefined;
  const rescheduledLocation =
    combined && rescheduled !== null ? locationById(rescheduled.branchId) : undefined;
  const selectedBranchId = selectedLocation?.branch.id ?? null;
  useEffect(() => {
    setPanelCodes(null);
    if (selectedBranchId === null) return;
    const controller = new AbortController();
    atLocation(selectedBranchId, () => listProcedureCodes(false, controller.signal))
      .then(setPanelCodes)
      .catch(() => setPanelCodes(null));
    return () => controller.abort();
  }, [selectedBranchId]);

  /** A clinician's name, for column heads and cards. */
  const providerName = (providerId: string): string => {
    const member = staff.find((person) => person.id === providerId);
    if (member !== undefined) return `${member.firstName} ${member.lastName}`;
    for (const location of [...shownLocations, ...(week?.locations ?? [])]) {
      const found = location.providers.find((person) => person.userId === providerId);
      if (found !== undefined) return found.name;
    }
    return "Unknown clinician";
  };

  // ── Filters (CF-D16): what is in view, and what each section offers ────────
  const viewRows: Appointment[] =
    view === "week"
      ? (week?.locations ?? []).flatMap((location) => location.appointments)
      : onScreen;
  /** Real work only: a patient, and not cancelled or missed. */
  const inScope = viewRows.filter(
    (row) => row.patient !== null && statusGroupOf(row.status) !== null,
  );
  const monthShown =
    month?.locations
      .filter((location) => !filters.branchIds.includes(location.branch.id))
      .reduce((sum, location) => sum + location.days.reduce((s, d) => s + d.count, 0), 0) ?? 0;
  const total = view === "month" ? monthShown : inScope.length;
  const showing =
    view === "month" ? monthShown : inScope.filter((row) => passesFilters(row, filters)).length;
  const counted = (match: (row: Appointment) => boolean): number | null =>
    view === "month" ? null : inScope.filter(match).length;

  /** The locations the filters and the week work from. */
  const optionSource: readonly CombinedLocation[] =
    view === "week" ? (week?.locations ?? []) : (combinedDay?.locations ?? []);
  const filterLocations: FilterLocation[] = calendarLocations.reachable
    .filter((location) => shownIds.includes(location.id))
    .map((location) => ({
      id: location.id,
      name: location.name,
      code: location.code,
      hue: location.hue,
    }));
  const filterOptions: Record<FilterGroup, FilterOption[]> = {
    branchIds: filterLocations.map((location) => ({
      value: location.id,
      label: location.name,
      count: counted((row) => row.branchId === location.id),
      pre: <LocationBadge code={location.code} hue={location.hue} name={location.name} />,
    })),
    providerIds: (() => {
      const ids = new Set<string>();
      if (combined || view !== "day") {
        for (const location of optionSource)
          for (const person of location.providers) ids.add(person.userId);
      } else {
        for (const person of providerChoicesForFilter()) ids.add(person);
      }
      for (const row of inScope) if (row.providerId !== null) ids.add(row.providerId);
      return [...ids]
        .map((id) => ({
          value: id,
          label: providerName(id),
          count: counted((row) => row.providerId === id),
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    })(),
    chairIds:
      combined || view !== "day"
        ? optionSource.flatMap((location) =>
            location.chairs.map((chair) => ({
              value: chair.id,
              label: chair.name,
              branchId: location.branch.id,
              count: counted((row) => row.chairId === chair.id),
            })),
          )
        : (roster?.chairs ?? []).map((chair) => ({
            value: chair.chairId,
            label: chair.chairName,
            count: counted((row) => row.chairId === chair.chairId),
          })),
    types: [
      ...categories.map((category) => ({
        value: category.id,
        label: category.name,
        count: counted((row) => appointmentTypeOf(row) === category.id),
        pre: (
          <i
            className={styles.swatch}
            style={{ background: categoryColour(category.colour) }}
            aria-hidden="true"
          />
        ),
      })),
      {
        value: FIRST_VISIT_TYPE,
        label: "First visit",
        count: counted((row) => appointmentTypeOf(row) === FIRST_VISIT_TYPE),
        pre: <em className={dayStyles.newTag}>New</em>,
      },
      // Bookings whose procedure has no category, or that name no procedure.
      {
        value: NO_CATEGORY_TYPE,
        label: "No category",
        count: counted((row) => appointmentTypeOf(row) === NO_CATEGORY_TYPE),
      },
    ],
    statuses: CALENDAR_STATUS_GROUPS.map((group: CalendarStatusGroup) => ({
      value: group,
      label: CALENDAR_STATUS_GROUP_LABELS[group],
      count: counted((row) => statusGroupOf(row.status) === group),
      pre:
        group === "NOT_CONFIRMED" ? (
          <i className={dayStyles.conf} aria-hidden="true" />
        ) : group === "CONFIRMED" ? (
          <i className={`${dayStyles.conf} ${dayStyles.confOn}`} aria-hidden="true" />
        ) : undefined,
    })),
  };
  /** Who the single-location day offers: everyone the rota lists who treats. */
  function providerChoicesForFilter(): string[] {
    return availability
      .filter((person) => CLINICAL_ROLES.has(person.role))
      .map((person) => person.userId);
  }
  /** A column's name sets Only for it (CF-D16); pressing it again shows them all. */
  const onlyColumn = (group: "providerIds" | "chairIds"): string | null => {
    const all = filterOptions[group].map((option) => option.value);
    const visible = all.filter((value) => !filterState.isHidden(group, value));
    return visible.length === 1 && all.length > 1 ? (visible[0] ?? null) : null;
  };
  const columnOnly = (group: "providerIds" | "chairIds", id: string): void =>
    filterState.only(
      group,
      id,
      filterOptions[group].map((option) => option.value),
    );
  /**
   * **One doctor shown is one column** (CZ-D6): their bookings, each naming its
   * chair, under their name and the location's. Its head shows every doctor again.
   */
  const soloDoctorColumn = (): {
    id: string;
    name: string;
    where: string;
    onShowAll: () => void;
  } | null => {
    const doctorId = onlyColumn("providerIds");
    if (doctorId === null) return null;
    return {
      name: providerName(doctorId),
      id: doctorId,
      where: calendarLocations.reachable.find((location) => location.id === branchId)?.name ?? "",
      onShowAll: () => columnOnly("providerIds", doctorId),
    };
  };
  /** The shown locations' zones, named in the header when they differ (CF-D14). */
  const zones = [...new Set(shownLocations.map((location) => location.branch.timezone))];

  const canBook =
    (combined && bookingBranchId === "") ||
    (chairId !== "" &&
      (isBlock || patient !== null) &&
      // Each child says what they come for, which is also how long they take (FV-D2).
      (isBlock || family.every((member) => member.procedureCodeIds.length > 0)) &&
      durationMinutes > 0 &&
      // A per-tooth procedure's course cannot be started without its tooth (TP12),
      // and since QA4-D4 neither can a one-visit procedure done on a tooth.
      !((chainSource?.needsTooth === true || asksToothOnce) && chainTeeth.length === 0));
  /**
   * The booking form's own options — a retired chair is not one of them. With
   * several locations shown, the chosen location's chairs only (CF-D15).
   */
  const activeChairs: { id: string; name: string }[] = combined
    ? (bookingLocation?.chairs ?? [])
    : chairs.filter((chair) => chair.isActive);

  /**
   * Clicking, or clicking and dragging, empty space (client request,
   * 2026-08-24) opens the same form "New appointment" does, seeded with the
   * chair and the time already chosen — the patient and procedure are still
   * what it exists to ask for. The chosen slot stays drawn as a shadow on the
   * grid while the rail asks who it is for (client, 2026-08-28). The quick
   * ring's *Book here* is this too (STORY-221).
   */
  const quickBook = (quickChairId: string, startMinute: number, endMinute: number): void => {
    setSelectedId(null);
    setChairId(quickChairId);
    setClock(clockOfMinutes(startMinute));
    setEndClock(clockOfMinutes(endMinute));
    setIsBlock(false);
    setBlockReasonId("");
    setPatient(null);
    setPatientQuery("");
    setSummary("");
    setProcedureCodeId(null);
    // From one doctor's column (CZ-D6) the doctor is known and the chair is asked.
    setProviderId(quickChairId === "" ? (onlyColumn("providerIds") ?? "") : "");
    setBooking(true);
  };

  /**
   * Clicking empty space in a combined chair's column fills the location and
   * the chair (CF-D15); the rest is asked. The quick ring's *Book here* too.
   */
  const slotBook = (slotBranchId: string, slotChairId: string, startMinute: number): void => {
    setSelectedId(null);
    setBookingBranchId(slotBranchId);
    setChairId(slotChairId);
    setClock(clockOfMinutes(startMinute));
    setEndClock(clockOfMinutes(startMinute + 30));
    setIsBlock(false);
    setBlockReasonId("");
    setPatient(null);
    setPatientQuery("");
    setSummary("");
    setProcedureCodeId(null);
    setProviderId("");
    setBooking(true);
  };

  /** The header's *New appointment* button — and the ring's (STORY-221). */
  const newAppointment = (): void => {
    setSelectedId(null);
    // With several locations shown nothing chooses the location for you (CF-D15).
    setBookingBranchId("");
    setBooking(true);
  };

  /** The header's *Today* button. It moves the month too, which the month view reads. */
  const goToToday = (): void => {
    setDay(today());
    setMonthKey(today().slice(0, 7));
  };

  /** The ⋯ menu's *Edit chairs*. */
  const openEditChairs = (): void => {
    // Re-read on open, not only after a change made inside the dialog — a
    // booking made moments ago already changed a chair's appointment count,
    // and the retire warning has to be right about that the first time.
    void reloadChairs();
    void reloadRoster();
    setEditingChairs(true);
  };

  /** Home's ring asks for a new appointment with `/calendar?ring=new` (STORY-221). */
  const newHonoured = useRef(false);
  const mayBook = can("appointments.book");
  useEffect(() => {
    if (wantedRing !== "new" || wantedId !== null || newHonoured.current || !mayBook) return;
    newHonoured.current = true;
    newAppointment();
    // `newAppointment` only sets state; the request is honoured once.
  }, [wantedRing, wantedId, mayBook]);

  // ── The quick ring, per thing (STORY-221, QR7–QR9) ──────────────────────────
  // Every action below is a button this screen already has, pressed for you.

  /** The day's title, for a ring's second line. */
  const shownTitle = titleFor(view, day, monthKey);

  useRingProvider("page", () => ({
    ring: "page.calendar",
    title: "Calendar",
    meta: shownTitle,
    actions: [
      {
        key: "new",
        label: "New appointment",
        icon: RING_ICONS.book,
        needs: ["appointments.book"],
        hint: "Opens the booking form",
        run: newAppointment,
      },
      {
        key: "wait",
        label: "Waitlist",
        icon: RING_ICONS.list,
        needs: ["waitlist.manage"],
        hint: "Opens the waitlist on the left",
        run: () => {
          setView("day");
          setSide("waitlist");
        },
      },
      {
        key: "wa",
        label: "WhatsApp requests",
        icon: RING_ICONS.whatsapp,
        needs: ["appointments.validate"],
        module: "CHATBOT",
        hint: "Bookings the agent is waiting on",
        run: () => {
          setView("day");
          setSide("whatsapp");
        },
      },
      !showingToday(view, day, monthKey, today()) && {
        key: "today",
        label: "Go to today",
        icon: RING_ICONS.today,
        hint: "Jumps back to today",
        run: goToToday,
      },
      {
        key: "chairs",
        label: "Edit chairs",
        icon: RING_ICONS.chair,
        needs: ["settings.locations.manage"],
        hint: "Chairs, turnover and who sits where",
        run: openEditChairs,
      },
    ],
  }));

  useRingProvider("cal.visit", (element) => {
    const appointment = onScreen.find((row) => row.id === element.dataset.id);
    if (appointment === undefined) return null;
    const at = combined ? locationById(appointment.branchId) : undefined;
    const zone = at?.branch.timezone ?? roster?.frame.timezone ?? timezone;
    const chairName =
      (at?.chairs ?? chairs).find((chair) => chair.id === appointment.chairId)?.name ?? null;
    const from = wallClockOfInstant(new Date(appointment.startsAt), zone).clock;
    const to = wallClockOfInstant(new Date(appointment.endsAt), zone).clock;
    const who = appointment.patient;
    const name = who === null ? "Chair held" : `${who.firstName} ${who.lastName}`.trim();
    const first = who?.firstName ?? "They";
    const step = who === null ? null : visitStep(appointment.status);
    const live = isMovable(appointment);
    const canCancel = nextStatuses(appointment.status).includes("CANCELLED");
    return {
      ring: "cal.visit",
      title: name,
      meta: [`${from}–${to}`, chairName, statusLabel(appointment.status)]
        .filter((part): part is string => part !== null)
        .join(" · "),
      patientId: who?.id ?? null,
      subject: { type: "booking", id: appointment.id },
      actions: [
        step !== null && {
          key: "step",
          label: step.label,
          icon: step.icon,
          needs: ["appointments.status"],
          hint: step.hint,
          run: () => askPanel(appointment.id, "step"),
        },
        live && {
          key: "move",
          label: "Reschedule",
          icon: RING_ICONS.move,
          needs: ["appointments.move"],
          hint: "Pick a new time or chair",
          // The panel's *Reschedule* button does exactly this (`onReschedule`).
          run: () => setRescheduling(appointment.id),
        },
        // The panel's payment section exists only once a visit is complete (STORY-187).
        who !== null &&
          appointment.status === "COMPLETED" && {
            key: "pay",
            label: "Take payment",
            icon: RING_ICONS.pay,
            needs: ["billing.payment.record"],
            hint: "Records a payment for this visit",
            run: () => askPanel(appointment.id, "pay"),
          },
        who !== null &&
          beforeArrival(appointment.status) && {
            key: "remind",
            label: "Remind",
            icon: RING_ICONS.bell,
            needs: ["reminders.send"],
            module: "CHATBOT",
            hint: `Sends ${first} a WhatsApp reminder`,
            run: () => askPanel(appointment.id, "remind"),
          },
        canCancel && {
          key: "cancel",
          label: "Cancel",
          icon: RING_ICONS.cancel,
          needs: ["appointments.status", "appointments.cancel"],
          danger: true,
          hint: "Cancels this booking",
          run: () => askPanel(appointment.id, "cancel"),
        },
        who !== null && {
          key: "file",
          label: "Open file",
          icon: RING_ICONS.file,
          needs: ["patients.view"],
          hint: "Opens their file",
          run: () => navigate(`/patients/${who.id}`),
        },
      ],
    };
  });

  /**
   * An empty slot: a combined chair's half-hour button carries its location,
   * chair and minute; a day grid's chair column carries its chair and the
   * minute last under the pointer (`data-ring-minute`).
   */
  useRingProvider("cal.slot", (element) => {
    if (!mayBook) return null;
    const minute = Number(element.dataset.ringMinute ?? element.dataset.minute);
    const slotChairId = element.dataset.chairId;
    if (!Number.isFinite(minute) || slotChairId === undefined) return null;
    const slotBranchId = element.dataset.branchId;
    const at = slotBranchId === undefined ? undefined : locationById(slotBranchId);
    const chairList = at?.chairs ?? chairs;
    const chairName =
      slotChairId === ""
        ? (soloDoctorColumn()?.name ?? "This doctor")
        : (chairList.find((chair) => chair.id === slotChairId)?.name ?? "This chair");
    const clockText = clockOfMinutes(minute);
    const book = (): void => {
      if (slotBranchId === undefined) quickBook(slotChairId, minute, minute + SNAP_MINUTES);
      else slotBook(slotBranchId, slotChairId, minute);
    };
    return {
      ring: "cal.slot",
      title: chairName,
      meta: `${clockText} · free`,
      actions: [
        {
          key: "book",
          label: "Book here",
          icon: RING_ICONS.book,
          needs: ["appointments.book"],
          hint: `Books ${chairName} at ${clockText}`,
          run: book,
        },
        {
          key: "newp",
          label: "New patient here",
          icon: RING_ICONS.personplus,
          needs: ["appointments.book", "patients.create"],
          hint: "Someone not on file yet",
          run: () => {
            book();
            setAddingPatient(true);
          },
        },
        {
          key: "fill",
          label: "Fill from waitlist",
          icon: RING_ICONS.list,
          needs: ["waitlist.manage"],
          hint: "Opens the waitlist, to drag someone here",
          run: () => {
            setView("day");
            setSide("waitlist");
          },
        },
        {
          key: "hold",
          label: "Hold the chair",
          icon: RING_ICONS.lock,
          needs: ["appointments.book"],
          hint: `Blocks ${chairName} from ${clockText}, with a reason`,
          run: () => {
            book();
            setIsBlock(true);
          },
        },
      ],
    };
  });

  return (
    <div className={styles.page} data-testid="calendar-page">
      {/*
        **The header, modernised** (CF-D17): day arrows around Today, the date,
        a pill saying what is shown, Day / Week / Month as one segmented
        control, and — on a day with several locations — Doctors / Chairs.
      */}
      <div className={styles.head}>
        <div className={styles.headMain}>
          <div className={styles.titleRow}>
            <span className={styles.dayNav}>
              <button
                type="button"
                className={styles.dateStep}
                onClick={() => {
                  if (view === "month") setMonthKey((current) => shiftMonth(current, -1));
                  else setDay((current) => shiftDay(current, view === "week" ? -7 : -1));
                }}
                aria-label={
                  view === "month"
                    ? "Previous month"
                    : view === "week"
                      ? "Previous week"
                      : "Previous day"
                }
                data-testid="calendar-prev"
              >
                &lsaquo;
              </button>
              {/*
                **Today**, between the arrows. Disabled once it would change
                nothing, so it reports where you are as well as where it takes
                you. It moves the month too, which the month view reads.
              */}
              <button
                type="button"
                className={styles.today}
                disabled={showingToday(view, day, monthKey, today())}
                onClick={goToToday}
                data-testid="calendar-today"
              >
                Today
              </button>
              <button
                type="button"
                className={styles.dateStep}
                onClick={() => {
                  if (view === "month") setMonthKey((current) => shiftMonth(current, 1));
                  else setDay((current) => shiftDay(current, view === "week" ? 7 : 1));
                }}
                aria-label={
                  view === "month" ? "Next month" : view === "week" ? "Next week" : "Next day"
                }
                data-testid="calendar-next"
              >
                &rsaquo;
              </button>
            </span>

            {/*
              A real date input, sized over the title and made invisible rather
              than styled to look like one — the browser's own picker, keyboard
              behaviour and accessibility land for free this way.
            */}
            <div className={styles.titleWrap}>
              <h1 className={styles.title}>{titleFor(view, day, monthKey)}</h1>
              {view !== "month" && (
                <input
                  ref={dateInputRef}
                  type="date"
                  className={styles.dateInput}
                  value={day}
                  onChange={(event) => setDay(event.target.value)}
                  aria-label="Day"
                  data-testid="calendar-day"
                />
              )}
            </div>

            {branchId !== null && (
              <span className={styles.scope} data-testid="calendar-scope">
                {combined
                  ? `${String(shownIds.length)} locations in one calendar`
                  : (calendarLocations.reachable.find((location) => location.id === branchId)
                      ?.name ?? "This location")}
              </span>
            )}

            {/* The queues, New appointment and ⋯ at the far right of the title (CZ-D8). */}
            <div className={styles.titleActions}>
              {/*
            **The two queues, as counters** (CS6). Each opens the side panel on
            its own list, and pressing the open one shuts it. Warm when
            something waits, so a glance at the header says whether to look.
            The day view only: the panel sits beside the day's grid, which is
            where a waiting patient gets dragged to.
          */}
              {can("waitlist.manage") && (
                <button
                  type="button"
                  className={(waitlistCount ?? 0) > 0 ? styles.queueHot : styles.queue}
                  aria-pressed={view === "day" && side === "waitlist"}
                  onClick={() => {
                    setView("day");
                    setSide((open) => (open === "waitlist" ? null : "waitlist"));
                  }}
                  data-testid="side-waitlist"
                >
                  Waitlist <span className={styles.queueCount}>{waitlistCount ?? "…"}</span>
                </button>
              )}
              {/* Chatbot's queue: hidden outright without it (QF-D9, MS-D5). */}
              {hasChatbot && can("appointments.validate") && (
                <button
                  type="button"
                  className={(waitingCount?.total ?? 0) > 0 ? styles.queueHot : styles.queue}
                  aria-pressed={view === "day" && side === "whatsapp"}
                  onClick={() => {
                    setView("day");
                    setSide((open) => (open === "whatsapp" ? null : "whatsapp"));
                  }}
                  data-testid="side-whatsapp"
                >
                  WhatsApp <span className={styles.queueCount}>{waitingCount?.total ?? "…"}</span>
                </button>
              )}

              {can("appointments.book") && (
                <button
                  type="button"
                  className={styles.newAppointment}
                  onClick={newAppointment}
                  data-testid="new-appointment-button"
                >
                  New appointment
                </button>
              )}

              {/*
            **Edit chairs, behind ⋯** (CS6) — a settings action, not what this
            screen is for. Still admin-only, matching STORY-009's narrowing of
            §3.3 for creating or changing a chair; the server refuses anyone
            else with a 403 regardless.
          */}
              {can("settings.locations.manage") && (
                <RowMenu
                  label="More calendar actions"
                  testId="calendar-more"
                  items={[
                    {
                      label: "Edit chairs",
                      testId: "edit-chairs-button",
                      onSelect: openEditChairs,
                    },
                  ]}
                />
              )}
            </div>
          </div>
          {/* Locations on different clocks each keep their own (CF-D14), and say so. */}
          {combined && zones.length > 1 && (
            <p className={styles.zones} data-testid="calendar-zones">
              Each location on its own clock:{" "}
              {shownLocations
                .map((location) => `${location.branch.code} ${location.branch.timezone}`)
                .join(" · ")}
            </p>
          )}
        </div>

        <div className={styles.headActions}>
          <div className={styles.segment} role="group" aria-label="Calendar view">
            {(["day", "week", "month"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  // Month opens on the month of the day being looked at.
                  if (option === "month" && view !== "month") setMonthKey(day.slice(0, 7));
                  setView(option);
                }}
                data-testid={"calendar-view-" + option}
                aria-pressed={view === option}
              >
                {option === "day" ? "Day" : option === "week" ? "Week" : "Month"}
              </button>
            ))}
          </div>

          {/*
            **Zoom, as hours on screen** (CZ-D1): in shows fewer hours, each
            taller, paged with the arrows above the clock; out returns to the
            whole day. The day view only — week and month have no hours to page.
          */}
          {view === "day" && (
            <div
              className={`${styles.segment ?? ""} ${styles.zoom ?? ""}`}
              role="group"
              aria-label="Zoom"
            >
              <button
                type="button"
                onClick={() => setHoursShown(stepZoom(chosenHours, -1, capacity))}
                disabled={isPreset(hoursShown) || !canZoomOut(chosenHours, capacity)}
                aria-label="Zoom out"
                title="Zoom out: more hours"
                data-testid="calendar-zoom-out"
              >
                −
              </button>
              {/*
                **What is shown, as a menu** (CZ-D5): every number of hours that
                fits, and the whole day, the morning and the afternoon. A menu
                rather than three more buttons, which wrapped the toolbar at 1280.
              */}
              <select
                className={styles.zoomLabel}
                aria-label="Hours shown"
                value={hoursShown === null ? "" : String(hoursShown)}
                onChange={(event) => {
                  const picked = event.target.value;
                  const preset = PRESETS.find((option) => option.value === picked);
                  setHoursShown(
                    preset !== undefined ? preset.value : picked === "" ? null : Number(picked),
                  );
                }}
                data-testid="calendar-zoom-label"
              >
                <option value="">{zoomLabel(drawnHours)}</option>
                {ZOOM_STEPS.filter(
                  (step): step is number =>
                    step !== null && (capacity === null || step < capacity.fits),
                ).map((step) => (
                  <option key={step} value={String(step)}>
                    {zoomLabel(step)}
                  </option>
                ))}
                {PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setHoursShown(stepZoom(chosenHours, 1, capacity))}
                disabled={!isPreset(hoursShown) && drawnHours === ZOOM_STEPS[ZOOM_STEPS.length - 1]}
                aria-label="Zoom in"
                title="Zoom in: fewer hours, taller"
                data-testid="calendar-zoom-in"
              >
                +
              </button>
            </div>
          )}

          {combined && view === "day" && (
            <div className={styles.segment} role="group" aria-label="Columns">
              {(["doctors", "chairs"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setColumnsBy(option)}
                  aria-pressed={columnsBy === option}
                  data-testid={"calendar-columns-" + option}
                >
                  {option === "doctors" ? "Doctors" : "Chairs"}
                </button>
              ))}
            </div>
          )}

          {/* The filters, where the queues and New appointment were (CZ-D8). */}
          {branchId !== null && (
            <FilterBar
              id="calendar-filters"
              state={filterState}
              options={filterOptions}
              locations={combined ? filterLocations : []}
              doctorSignedIn={meAsDoctor !== null}
            />
          )}
        </div>
      </div>

      {/*
        **The toolbar** (CZ-D7, CZ-D8): the live count and the key; the filter
        chips sit in the row above.
      */}
      {branchId !== null && (
        <div className={styles.toolbar}>
          <span
            className={styles.countLine}
            role="status"
            aria-live="polite"
            data-testid="filter-count"
          >
            {notice !== null ? (
              <span className={styles.notice} data-testid="calendar-notice">
                {notice}
              </span>
            ) : showing === total ? (
              ""
            ) : (
              `Showing ${String(showing)} of ${String(total)}`
            )}
          </span>
          <span className={styles.key} aria-label="Key">
            {categories.map((category) => (
              <span key={category.id}>
                <i
                  className={styles.swatch}
                  style={{ background: categoryColour(category.colour) }}
                />
                {category.name}
              </span>
            ))}
            <span>
              <em className={dayStyles.newTag}>New</em>First visit
            </span>
            <span>
              <i className={`${dayStyles.conf} ${dayStyles.confOn}`} />
              Confirmed
            </span>
            <span>
              <i className={dayStyles.conf} />
              Not confirmed
            </span>
          </span>
        </div>
      )}

      {rescheduled !== null && (
        // At the booking's own location, with that location's chairs (CF-D15).
        <BookingLocationProvider
          value={
            rescheduledLocation === undefined
              ? null
              : {
                  branchId: rescheduledLocation.branch.id,
                  name: rescheduledLocation.branch.name,
                  permissions: rescheduledLocation.branch.permissions,
                }
          }
        >
          <ReschedulePicker
            appointment={rescheduled}
            chairName={
              (rescheduledLocation?.chairs ?? chairs).find(
                (chair) => chair.id === rescheduled.chairId,
              )?.name ?? "this chair"
            }
            // Active chairs only: the picker must not offer a room that has been
            // retired, for the same reason the booking form stopped offering one.
            chairs={rescheduledLocation?.chairs ?? chairs.filter((chair) => chair.isActive)}
            onMove={(startsAt, chairId) => moveKeepingChain(rescheduled, startsAt, chairId)}
            onMoved={() => {
              setRescheduling(null);
              void reload();
            }}
            onClose={() => setRescheduling(null)}
          />
        </BookingLocationProvider>
      )}

      {shiftAsk !== null && (
        <ShiftDialog later={shiftAsk.later} onAnswer={(answer) => shiftAsk.resolve(answer)} />
      )}

      {editingChairs && branchId !== null && (
        <EditChairs
          branchId={branchId}
          chairs={chairs}
          day={day}
          roster={roster}
          staff={staff}
          onChanged={() => {
            // Every write this dialog can make touches something the grid or
            // the booking form already trusts: a new or retired chair changes
            // `chairs` and the day's own columns (`roster`), and a changed
            // turnover rewrites today's and the future's appointments — so
            // all three are re-read rather than guessing which one changed.
            void reloadChairs();
            void reloadRoster();
            void reload();
          }}
          onClose={() => setEditingChairs(false)}
        />
      )}

      {/*
        **Two different nothings, and they used to share one sentence.**

        `branchId === null` means either *the switcher has not resolved one yet*
        or *this clinic has no location at all* — and until self-serve sign-up
        the second case barely existed, because a clinic came from the seed or
        an operator and had branches within the minute. A practice that has just
        created itself hits it immediately, and got *"Choosing a branch…"*
        forever: a loading message that never finishes, on the second item in
        the sidebar, with nothing to click.

        `unknown` deliberately falls through to the old wording. A server that
        did not answer must not be reported as a clinic with no locations.
      */}
      {/*
        **Three nothings now, because the second one was lying** (2026-09-21).

        `GET /branches` answers *"which locations may **you** work in"* — it
        returns every branch to an admin and only their own to everybody else
        (`BranchesService.list`). `useHasBranches` read that as a fact about the
        **clinic**, so a dentist holding no membership was told the practice had
        no location, about a practice with a location and a chair that their
        colleague could see perfectly well. Found on production.

        The message then sent them to `/setup`, which is admin only (S3) and
        which a dentist cannot open — a dead end with directions to another
        dead end.

        **`canAdminister` is what separates them**, because it is exactly the
        privilege that makes the branch list clinic-wide. An admin seeing none
        really is looking at a practice with none. Anybody else seeing none has
        learnt nothing about the clinic, only about themselves.
      */}
      {branchId === null && hasBranches === "none" && can("settings.locations.manage") && (
        <p className={styles.wanting} data-testid="calendar-no-branch">
          There is no location to show a day for yet. <Link to="/setup">Finish setting up</Link> —
          the calendar draws one column per chair, so it needs a location with at least one chair in
          it.
        </p>
      )}

      {branchId === null && hasBranches === "none" && !can("settings.locations.manage") && (
        <p className={styles.wanting} data-testid="calendar-no-branch">
          You have not been added to a location yet, so there is no day to draw. Ask an
          administrator at this practice to add you to one — the calendar draws a column per chair,
          and it needs a location you work in.
        </p>
      )}

      {branchId === null && hasBranches !== "none" && (
        <p className={styles.empty} data-testid="calendar-no-branch">
          Choosing a branch…
        </p>
      )}

      {/* The filters are a bar in the toolbar now (CZ-D7); the waitlist has the left. */}
      <div className={styles.calBody}>
        <div className={styles.calMain}>
          {view === "week" && week !== null && (
            <CombinedWeek
              week={week}
              filters={filters}
              today={today()}
              onOpenDay={(date) => {
                // Clicking a day opens that day's grid — the whole point of a week
                // being for planning.
                setDay(date);
                setView("day");
              }}
            />
          )}

          {view === "month" && month !== null && (
            <CombinedMonthView
              month={month}
              hiddenBranches={filters.branchIds}
              today={today()}
              onOpenDay={(date) => {
                setDay(date);
                setView("day");
              }}
              onPrevious={() => setMonthKey((current) => shiftMonth(current, -1))}
              onNext={() => setMonthKey((current) => shiftMonth(current, 1))}
            />
          )}

          {view === "month" && month === null && branchId !== null && (
            <p className={styles.empty}>Reading that month&rsquo;s load…</p>
          )}

          {view === "week" && week === null && branchId !== null && (
            <p className={styles.empty}>Reading that week&rsquo;s load…</p>
          )}

          {view === "day" &&
            branchId !== null &&
            (combined ? combinedDay === null : appointments === null || roster === null) && (
              <p className={styles.empty}>Reading the day…</p>
            )}

          {view === "day" &&
            (combined ? combinedDay !== null : appointments !== null && roster !== null) && (
              /*
          **Two columns once something is selected, one when nothing is** — the
          mockup's right rail, which only exists when it has something to say.
          Reserving the column permanently would narrow the grid all day for a
          panel that is empty most of it.

          **Booking no longer opens it** (T6). The form is a dialog over the
          grid now, so a booking in progress must not narrow the day behind it:
          the whole point of a dialog is that what is underneath is unchanged
          when it closes.
        */
              <div
                className={`${selected === null ? styles.dayOnly : styles.dayWithPanel} ${
                  shownSide === null ? "" : styles.withSide
                }`}
              >
                {/*
            **The side panel** (CS6): one queue at a time, opened from the
            header's counters. The mini month that sat above them is gone —
            the date title opens a picker, so no way of reaching a day was lost
            (it reverses the client's 2026-09-07 request, knowingly).
          */}
                <div className={styles.rail} hidden={shownSide === null} data-testid="side-panel">
                  {/* Each queue names itself, so the panel's own head is only the way out. */}
                  <div className={styles.sideHead}>
                    {can("waitlist.manage") && hasChatbot && can("appointments.validate") && (
                      <div className={styles.sideTabs} role="tablist" aria-label="Queues">
                        <button
                          type="button"
                          role="tab"
                          aria-selected={side === "waitlist"}
                          onClick={() => setSide("waitlist")}
                          data-testid="side-tab-waitlist"
                        >
                          Waitlist
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={side === "whatsapp"}
                          onClick={() => setSide("whatsapp")}
                          data-testid="side-tab-whatsapp"
                        >
                          WhatsApp
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      className={styles.sideClose}
                      onClick={() => setSide(null)}
                      aria-label="Close the side panel"
                      data-testid="side-close"
                    >
                      ×
                    </button>
                  </div>

                  {/*
              Who is waiting for an earlier slot. The calendar is where a slot
              comes free and where somebody would think to add a patient
              wanting one.
            */}
                  <div hidden={side !== "waitlist"}>
                    {can("waitlist.manage") && (
                      <WaitlistStrip
                        branchId={branchId}
                        version={waitlistVersion}
                        onChanged={() => {
                          // The queue changed, so who could fill a freed slot may have too.
                          void reload();
                        }}
                        /*
                  Which entry is under the cursor, so the grid's drop can resolve
                  it without the patient's details riding in the drag payload
                  (Anthony's QA, item 14). Cleared on drag end, including a drag
                  abandoned outside any column.
                */
                        onDragEntry={setDragging}
                        onCount={setWaitlistCount}
                      />
                    )}
                  </div>

                  {/*
              The one route into the calendar a human has to agree to (P3 Q4).
            */}
                  <div hidden={side !== "whatsapp"}>
                    {/* A clinic without Chatbot has no queue, and no button opens this (QF-D9). */}
                    {hasChatbot && can("appointments.validate") && (
                      <RequestQueue
                        branchId={branchId}
                        timezone={roster?.frame.timezone ?? "UTC"}
                        onDecided={() => {
                          // Approving moves a booking, so the day on screen is now stale.
                          void reload();
                        }}
                        /*
            **Declining a booking lives on the appointment panel, not in the
            queue** (STORY-057) — it cancels a booking the patient believes they
            have, and the reason box guards it there. So the queue's *Open*
            takes the reader to it, which means moving the day: an unvalidated
            booking is not necessarily on the day being looked at, and the queue
            deliberately lists every one of them (HC3).
          */
                        onOpen={(date, appointmentId) => {
                          setDay(date);
                          setSelectedId(appointmentId);
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* One grid item, not two: the day layout is a CSS grid whose columns are
              the side panel, the day and the appointment panel, so a loose key beside
              the grid took a column of its own and shoved the day to the right. */}
                <div className={styles.dayColumn} data-testid="day-column">
                  {combined && combinedDay !== null && (
                    <CombinedDayGrid
                      date={day}
                      locations={combinedDay.locations}
                      // One doctor shown: their own column, each booking badged (CZ-D6).
                      columns={onlyColumn("providerIds") === null ? columnsBy : "doctors"}
                      filters={filters}
                      providerName={providerName}
                      onlyColumn={onlyColumn(columnsBy === "doctors" ? "providerIds" : "chairIds")}
                      selectedId={selectedId}
                      onSelect={(appointment) => {
                        setBooking(false);
                        setSelectedId(appointment.id);
                      }}
                      onColumnName={columnOnly}
                      hoursShown={hoursShown}
                      onCapacity={setCapacity}
                      // Dragged within its own location (OQ-D13); refused onto another.
                      onMove={can("appointments.move") ? move : undefined}
                      onRefused={say}
                      onSlot={can("appointments.book") ? slotBook : undefined}
                    />
                  )}

                  {!combined && roster !== null && appointments !== null && (
                    <DayGrid
                      date={day}
                      frame={roster.frame}
                      chairs={roster.chairs.filter(
                        (chair) => !filters.chairIds.includes(chair.chairId),
                      )}
                      appointments={appointments.filter((appointment) =>
                        passesFilters(appointment, filters),
                      )}
                      providerName={(providerId) =>
                        providerId === null ? null : providerName(providerId)
                      }
                      onChairName={(chairId) => columnOnly("chairIds", chairId)}
                      onlyChairId={onlyColumn("chairIds")}
                      soloDoctor={soloDoctorColumn()}
                      hoursShown={hoursShown}
                      onCapacity={setCapacity}
                      externalBlocks={externalBlocks}
                      selectedId={selectedId}
                      onSelect={(appointment) => {
                        // The rail shows one thing at a time — opening a booking's
                        // details closes an in-progress "New appointment" the same way
                        // the mockup's own right rail does.
                        setBooking(false);
                        setSelectedId(appointment.id);
                      }}
                      onMove={can("appointments.move") ? move : undefined}
                      onQuickBook={quickBook}
                      /**
                       * A waiting patient dropped onto a gap (Anthony's QA, item 14).
                       *
                       * **The same booking form the grid's own quick-book opens**, seeded
                       * with the chair, the time it landed on, and — the part that makes
                       * this worth doing — the patient and the length their waitlist
                       * entry already records. The desk confirms; nothing books itself.
                       *
                       * **It does not take them off the queue.** Booking does that, and
                       * booking is what the form's own Save does. A drop that removed the
                       * entry and then had its booking refused by the turnover rule would
                       * lose the patient's place for nothing.
                       */
                      onDropWaiting={(entryId, droppedChairId, startMinute) => {
                        /**
                         * **Resolved from the entry the strip handed over, not from the
                         * drag payload.** Only the id travels in `dataTransfer` — a
                         * patient's name in there is readable by whatever application a
                         * drag is dropped into — so the entry itself comes across in
                         * React state and the id is what proves the two are the same drag.
                         */
                        const entry = dragging;
                        if (entry === null || entry.id !== entryId) return;

                        setSelectedId(null);
                        setChairId(droppedChairId);
                        setClock(clockOfMinutes(startMinute));
                        setEndClock(clockOfMinutes(startMinute + entry.durationMinutes));
                        setIsBlock(false);
                        setBlockReasonId("");
                        setPatientQuery("");
                        setSummary("");
                        setProcedureCodeId(entry.procedureCodeId);
                        setProviderId("");
                        setBooking(true);

                        /**
                         * **The whole patient is fetched rather than assembled** from the
                         * three fields the queue carries. It has `patientName` as one
                         * string and the form needs first and last separately; splitting
                         * on the first space mangles a middle name or a two-word surname,
                         * which is the same trap `NewPatientForm` documents in its own
                         * comment. One request costs nothing beside a drag.
                         *
                         * The form opens first and the name fills in a moment later —
                         * better than a form that appears only once the network answers.
                         */
                        void getPatient(entry.patientId)
                          .then((full) => setPatient(full))
                          .catch(() => {
                            // The slot and the length are still right, so the booking is
                            // still makeable — the desk picks the patient by hand.
                            setPatient(null);
                            setPatientQuery(entry.patientName);
                          });
                      }}
                    />
                  )}
                </div>

                {/*
            **A dialog since T6** (2026-08-31), not the mockup's right rail.
            Item 24 asked for a popup and the client took that reading with its
            cost named — a dialog covers the grid, so the dashed shadow they
            asked for on 2026-08-28 had nothing to sit on and is gone with it.

            It dismisses by clicking outside and by Escape, like every other
            dialog, and clears itself on the way out (item 27) so an abandoned
            entry is not waiting next time.
          */}
                {booking && (
                  <Modal
                    label="New appointment"
                    testId="booking-form"
                    panelClassName={styles.bookerPanel}
                    backdropClassName={styles.bookerBackdrop}
                    onClose={() => {
                      setBooking(false);
                      clearBookingForm();
                    }}
                  >
                    {/*
                **A sectioned card** (FX16): a tinted head saying what is being
                booked and when, the fields in two quiet groups, and the actions
                on a footer bar — over a dimmed, blurred day.
              */}
                    <div className={styles.bookerHead}>
                      <h2 className={styles.bookerTitle}>
                        {isBlock ? "Hold the chair" : "New appointment"}
                      </h2>
                      <span className={styles.bookerWhen} data-testid="book-when">
                        {activeChairs.find((chair) => chair.id === chairId)?.name ?? "No chair"}{" "}
                        &middot; {shortDate(day)} &middot;{" "}
                        <strong>
                          {clock}–{endClock}
                        </strong>
                      </span>
                    </div>

                    <div className={styles.bookerBody}>
                      {/*
                **Chair and times, always open** (FX17, reversing CS7's fold).
                The chair is a live dropdown from the start — no *Change* to
                press first, even when the grid already chose it.
              */}
                      <section className={styles.bookSection} aria-label="When">
                        <span className={styles.sectionLabel}>When</span>
                        {/*
                    **The location first, with several shown** (CF-D15): empty
                    by default and required, so a visit never lands somewhere
                    nobody chose. Changing it empties the chair, the doctor and
                    the procedure, which are that location's own.
                  */}
                        {combined && (
                          <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Location
                              <RequiredMark />
                            </span>
                            <select
                              className={styles.select}
                              value={bookingBranchId}
                              onChange={(event) => {
                                setBookingBranchId(event.target.value);
                                setChairId("");
                                setProviderId("");
                                setProcedureCodeId(null);
                                setSummary("");
                                setPlanVisitId("");
                                setError(null);
                              }}
                              data-testid="book-location"
                            >
                              <option value="">Choose the location</option>
                              {shownLocations.map((location) => (
                                <option key={location.branch.id} value={location.branch.id}>
                                  {location.branch.name}
                                </option>
                              ))}
                            </select>
                            <span className={styles.hint}>
                              {bookingLocation === undefined
                                ? "Nothing is chosen for you: pick where this visit happens."
                                : `Books into ${bookingLocation.branch.name}: its chairs and doctors only.`}
                            </span>
                          </label>
                        )}
                        <label className={styles.field}>
                          <span className={styles.fieldLabel}>
                            Chair
                            <RequiredMark />
                          </span>
                          <select
                            className={styles.select}
                            value={chairId}
                            onChange={(event) => setChairId(event.target.value)}
                            disabled={combined && bookingLocation === undefined}
                            data-testid="book-chair"
                          >
                            {/* Retired chairs stay in `chairs` for `EditChairs` to reactivate,
                      so this is where a booking is kept from landing in one. */}
                            {combined && bookingLocation === undefined && (
                              <option value="">Pick the location first</option>
                            )}
                            {combined && bookingLocation !== undefined && chairId === "" && (
                              <option value="">Choose a chair</option>
                            )}
                            {!combined && activeChairs.length === 0 && (
                              <option value="">No chairs in this branch</option>
                            )}
                            {activeChairs.map((chair) => (
                              <option key={chair.id} value={chair.id}>
                                {chair.name}
                              </option>
                            ))}
                          </select>
                        </label>

                        {/*
                **From and To on one row.** They are a pair — nobody sets one
                without looking at the other — and stacked they put two 40px
                rows and two labels where one row does, in a dialog that was
                nine stacked fields tall and scrolled on a laptop.
              */}
                        <div className={styles.fieldPair}>
                          <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                              From
                              <RequiredMark />
                            </span>
                            <input
                              type="time"
                              className={styles.input}
                              value={clock}
                              onChange={(event) => {
                                const next = event.target.value;
                                // Moving "from" carries "to" along with it, by the same
                                // amount, so the duration the user already chose survives
                                // — changing the start should not silently shorten (or
                                // invert) the booking.
                                const shifted =
                                  minutesOfClock(endClock) +
                                  (minutesOfClock(next) - minutesOfClock(clock));
                                setClock(next);
                                setEndClock(clockOfMinutes(shifted));
                              }}
                              data-testid="book-time"
                            />
                          </label>

                          <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                              To
                              <RequiredMark />
                            </span>
                            <input
                              type="time"
                              className={styles.input}
                              value={endClock}
                              onChange={(event) => setEndClock(event.target.value)}
                              aria-label="Ends at"
                              data-testid="book-time-to"
                            />
                          </label>
                        </div>
                      </section>

                      <section
                        className={styles.bookSection}
                        aria-label={isBlock ? "Why" : "Who and what"}
                      >
                        <span className={styles.sectionLabel}>
                          {isBlock ? "Why" : "Who and what"}
                        </span>

                        {/*
                **Why the chair is held** (STORY-046d, decision G12).

                This is the label that reaches the chair's Google calendar, and
                it is a row the practice configured — never `notes` or the
                free-text summary below, which are for clinical detail and would
                take a patient's name out of the building with them.

                **Absent when the list is empty**, rather than drawn as an empty
                select: a practice that has not filled its reasons in Settings
                books exactly as it did before, and the block still says the
                chair is busy.
              */}
                        {isBlock && blockReasons.length > 0 && (
                          <label className={styles.field}>
                            <span className={styles.fieldLabel}>Why</span>
                            <select
                              className={styles.input}
                              value={blockReasonId}
                              onChange={(event) => setBlockReasonId(event.target.value)}
                              data-testid="book-block-reason"
                            >
                              {/* Not choosing is allowed — the chair is held either way. */}
                              <option value="">Not saying</option>
                              {blockReasons.map((reason) => (
                                <option key={reason.id} value={reason.id}>
                                  {reason.label}
                                </option>
                              ))}
                            </select>
                            <span className={styles.hint}>
                              This is what appears on the chair&rsquo;s Google calendar.
                            </span>
                          </label>
                        )}

                        {!isBlock && (
                          <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                              Patient
                              <RequiredMark />
                            </span>
                            {patient === null ? (
                              <input
                                className={styles.input}
                                value={patientQuery}
                                onChange={(event) => setPatientQuery(event.target.value)}
                                placeholder="Name, phone or file number"
                                aria-label="Find a patient"
                                data-testid="book-patient-search"
                              />
                            ) : (
                              <button
                                type="button"
                                className={styles.chosen}
                                onClick={() => {
                                  setPatient(null);
                                  setFamily([]);
                                }}
                                data-testid="book-patient-chosen"
                              >
                                {patient.firstName} {patient.lastName} · change
                              </button>
                            )}

                            {matches.length > 0 && patient === null && (
                              <div className={styles.matches} data-testid="patient-matches">
                                {matches.map((match) => (
                                  <button
                                    key={match.id}
                                    type="button"
                                    className={styles.match}
                                    onClick={() => {
                                      setPatient(match);
                                      setMatches([]);
                                    }}
                                    data-card=""
                                    data-testid="patient-match"
                                  >
                                    {match.firstName} {match.lastName} · {match.fileNumberDisplay}
                                  </button>
                                ))}
                              </div>
                            )}

                            {/*
                    **Nobody on file yet?** (FX15) — create them without leaving
                    the booking. Only for whoever may create patients; the new
                    file is chosen the moment it is saved.
                  */}
                            {patient === null && can("patients.create") && (
                              <button
                                type="button"
                                className={styles.linkish}
                                onClick={() => setAddingPatient(true)}
                                data-testid="book-new-patient"
                              >
                                New patient — not on file yet
                              </button>
                            )}
                          </label>
                        )}

                        {/*
                          **The patient's critical alerts, once chosen** (PP-D1) —
                          a booking or a course started here sees them; nothing
                          is refused for them.
                        */}
                        {!isBlock && patient !== null && (
                          <CriticalAlerts patientId={patient.id} testId="book-critical" />
                        )}

                        {!isBlock && patient !== null && (
                          <FamilyMembers
                            first={patient}
                            firstProcedureCodeId={procedureCodeId}
                            members={family}
                            onChange={setFamily}
                            catalogue={oneVisitCatalogue}
                            onLength={(minutes) => {
                              if (minutes !== 0) {
                                setEndClock((end) => clockOfMinutes(minutesOfClock(end) + minutes));
                              }
                            }}
                            onNewPatient={() => {
                              setAddingFamily(true);
                              setAddingPatient(true);
                            }}
                            canCreate={can("patients.create")}
                          />
                        )}

                        {!isBlock && (
                          // A `<div>`, not a `<label>`: the searchable Select draws and
                          // associates its own label, and a second one wrapping it would
                          // announce the field twice.
                          //
                          // **"Provider" meant nothing to the client** — he asked what the
                          // field was for and whether it was where the treating dentist
                          // goes (item 14). It was, and the word is the glossary's term
                          // rather than the front desk's, so the label says the sentence.
                          <div className={styles.field}>
                            {/*
                    **Only who is rostered that day** — STORY-040's acceptance
                    check. A provider not scheduled is not offered, so the
                    calendar stops suggesting slots with nobody to fill them.

                    The server does **not** refuse an unrostered provider (P3
                    Q16): a rota is softer information than a chair being
                    physically occupied, and an emergency must never be
                    blocked by a rota nobody has updated. Anyone already
                    chosen therefore stays selectable, and the note below says
                    why they are unusual.
                  */}
                            {/*
                    **Searchable, because a clinic's list is long** — eighteen
                    options on the demo clinic alone, and a real practice's staff
                    list only grows. This is the same rule the patient file's
                    procedure picker follows: a search box over four options is
                    worse, over eighteen it is the only way to find a name.

                    The "not rostered" suffix moves to the row's hint, where it
                    reads as a note about the person rather than as part of their
                    name.
                  */}
                            <Select
                              id="book-provider"
                              label="Who is treating them"
                              value={providerId}
                              options={providerChoices.map((person) => ({
                                value: person.userId,
                                label: person.name,
                                // Only a real reason (FX19) — nothing for somebody
                                // with no rota at all.
                                hint: awayLabel(person.away),
                              }))}
                              onChange={setProviderId}
                              testId="book-provider"
                              placeholder="Not decided yet"
                              emptyLabel="Not decided yet"
                            />
                            {/*
                    §4.3's soft warning (QA #13) — stated, never enforced. Two
                    chairs at once is normal practice, so this says what it sees
                    and leaves the decision where it belongs.
                  */}
                            {providerClash !== null && (
                              <span
                                className={styles.providerWarning}
                                data-testid="book-provider-clash"
                              >
                                Already treating{" "}
                                {providerClash.appointment.patient === null
                                  ? "a held chair"
                                  : `${providerClash.appointment.patient.firstName} ${providerClash.appointment.patient.lastName}`}{" "}
                                {providerClash.from}–{providerClash.to} in{" "}
                                {chairs.find(
                                  (chair) => chair.id === providerClash.appointment.chairId,
                                )?.name ?? "another chair"}
                                . That is allowed — check it is intended.
                              </span>
                            )}
                          </div>
                        )}

                        {/*
                **The branch's own procedures, in a dropdown** (client's manual
                QA, item 25) — **beside** the text box, not instead of it, which
                is the shape the tooth chart already uses for the same job.
                Choosing one fills the field and **sets the end time from the
                procedure's own duration**, which is the fact the catalogue
                exists to carry: a molar root canal is ninety minutes whatever
                slot was dragged, and the drag was an estimate of the same thing.
                The end stays editable afterwards.

                The text box never goes away. The catalogue is per branch and
                ships empty (ADR-0008), a consultation or a follow-up is a real
                reason to hold a chair and is in no catalogue, and a form that
                could only offer catalogued work would be unusable on a clinic's
                first day.
              */}

                        <div className={styles.field}>
                          {/*
                  **The dropdown holds the choice** (item 15).

                  It used to reset to "Choose from the catalogue…" the instant
                  you picked something, and the procedure's name appeared in the
                  text box underneath — two controls, one of which forgot
                  immediately. That reads exactly as the client described it:
                  *"upon selecting a procedure please replace it in the
                  dropdown, dont add it below it"*. It now shows what is
                  selected, and picking again replaces it.

                  **The text box stays**, and is no longer where the catalogue's
                  answer lands. The catalogue is per branch and ships empty
                  (ADR-0008), and a consultation or a follow-up is a real reason
                  to hold a chair that is in no catalogue — so it is the
                  "describe it instead" path rather than a display of what was
                  picked.
                */}
                          {catalogue.length > 0 && (
                            <Select
                              id="book-summary-catalogue"
                              label="For"
                              value={procedureCodeId ?? ""}
                              options={[
                                ...catalogue.map((code) => ({
                                  value: code.id,
                                  label: code.label,
                                  hint: `${String(code.defaultDurationMinutes)} min`,
                                })),
                                {
                                  value: DESCRIBE_IT,
                                  label: "Something else — describe it",
                                  keywords: "free text other custom",
                                },
                              ]}
                              onChange={(next) => {
                                const code = catalogue.find((item) => item.id === next);
                                if (code === undefined) {
                                  // `DESCRIBE_IT` or the empty value — either way there is
                                  // no catalogue answer, so hand it to the text box.
                                  // "Something else" — hand it to the text box, which
                                  // appears for the purpose rather than waiting around.
                                  setProcedureCodeId(null);
                                  setSummary("");
                                  setDescribing(true);
                                  return;
                                }
                                setDescribing(false);
                                setSummary(code.label);
                                // **The link, and the whole point of TP18.** The picker
                                // already set the label and the duration; keeping the id
                                // is what lets the booking say what materials it needs
                                // and lets STORY-111 deduct against it.
                                setProcedureCodeId(code.id);
                                setEndClock(
                                  clockOfMinutes(
                                    minutesOfClock(clock) + code.defaultDurationMinutes,
                                  ),
                                );
                              }}
                              testId="book-summary-catalogue"
                              placeholder="Choose from the catalogue…"
                            />
                          )}

                          {/*
                  **The text box only when it is the answer** (client,
                  2026-09-07: *"the For section is annoying"*).

                  It used to sit under the catalogue permanently — and
                  *disabled*, reading "Taken from the catalogue above", once
                  something was picked. So the commonest path, choosing a
                  procedure, left a dead field on the form saying nothing, and
                  the rarer path had two controls where one would do. Exactly
                  the shape the tooth editor was rebuilt out of on 2026-09-07.

                  It appears when there is no catalogue to choose from — a
                  clinic's first day, ADR-0008 — or when somebody has explicitly
                  chosen "Something else", which is the only other way `summary`
                  gets written.
                */}
                          {(catalogue.length === 0 || describing) && (
                            <label className={styles.field}>
                              <span className={styles.fieldLabel}>For</span>
                              <input
                                className={styles.input}
                                value={summary}
                                onChange={(event) => {
                                  setSummary(event.target.value);
                                  // **Typing breaks the link, on purpose.** Picking
                                  // Whitening and then typing something else would leave
                                  // the booking claiming whitening's materials while the
                                  // screen said otherwise — a prep list that is
                                  // confidently wrong is worse than none.
                                  setProcedureCodeId(null);
                                }}
                                placeholder="Recall, extraction, maintenance…"
                                aria-label="What the booking is for"
                                data-testid="book-summary"
                              />
                              {catalogue.length > 0 && (
                                <button
                                  type="button"
                                  className={styles.linkish}
                                  onClick={() => {
                                    setDescribing(false);
                                    setSummary("");
                                    setProcedureCodeId(null);
                                  }}
                                  data-testid="book-summary-back"
                                >
                                  Choose from the catalogue instead
                                </button>
                              )}
                            </label>
                          )}
                        </div>

                        {/*
                **More than one procedure** (Emile, 2026-10-22) — a check-up and a
                filling in one sitting. Each one moves *To* on by its length.
              */}
                        {!isBlock && patient !== null && procedureCodeId !== null && (
                          <ProcedureTags
                            values={moreCodes}
                            catalogue={oneVisitCatalogue.filter(
                              (code) => code.id !== procedureCodeId,
                            )}
                            who={patient.firstName}
                            addLabel={`+ Another procedure for ${patient.firstName}`}
                            onChange={(next, minutes) => {
                              setMoreCodes(next);
                              if (minutes !== 0) {
                                setEndClock((end) => clockOfMinutes(minutesOfClock(end) + minutes));
                              }
                            }}
                          />
                        )}

                        {/*
                **Which plan visit this booking is** (client, 2026-09-06).

                Decision D6 added the link and put it on the appointment panel —
                *after* the booking existed — so saying a booking was part of a
                plan meant creating it and then editing it. This is the half
                that was missing.

                Only when a patient is chosen and they actually have a live
                plan waiting: most bookings are a single procedure and no plan
                is involved (TP4), and an empty picker on every booking would be
                a question nobody can answer.
              */}
                        {!isBlock && patient !== null && planVisitOptions.length > 0 && (
                          <Select
                            id="book-plan"
                            label="Part of a treatment plan?"
                            value={planVisitId}
                            options={planVisitOptions}
                            onChange={setPlanVisitId}
                            testId="book-plan"
                            placeholder="Not part of a plan"
                            emptyLabel="Not part of a plan"
                            note="Pick a plan to book its next visit and the ones after it."
                          />
                        )}

                        {/*
                The tooth a per-tooth multi-visit procedure is for, asked once
                (TP12) — **on the patient's own chart**, in a dialog, rather
                than a list of 32 FDI numbers (2026-09-24).
              */}
                        {!isBlock &&
                          patient !== null &&
                          (chainSource?.needsTooth === true || asksToothOnce) && (
                            <ToothPicker
                              patientId={patient.id}
                              value={chainTeeth}
                              onChange={setChainTeeth}
                              // Required for a one-tooth procedure too since QA4-D4.
                              required
                            />
                          )}

                        {!isBlock && patient !== null && (
                          <ChainVisits
                            rows={chain.rows}
                            loading={chain.loading}
                            include={chainInclude}
                            onInclude={setChainInclude}
                            onEdit={chain.edit}
                          />
                        )}
                      </section>

                      {error !== null && (
                        <p className={styles.error} data-testid="booking-error">
                          {error}
                        </p>
                      )}

                      {/*
                **The visits of a chain the server turned away** (CH10). Visit
                1 and the rest are booked; these are not, and wait to be booked
                (CH9) — said here, because a patient leaving with a date that
                was never booked is the outcome this exists to prevent.
              */}
                      {chainRefused.length > 0 && (
                        <div className={styles.refused} data-testid="chain-refused">
                          <span className={styles.refusedLabel}>
                            {chainRefused.length === 1
                              ? "One visit could not be booked — it is waiting to be booked"
                              : `${String(chainRefused.length)} visits could not be booked — they are waiting to be booked`}
                          </span>
                          {chainRefused.map((row) => (
                            <span
                              key={row.partId}
                              className={styles.refusedRow}
                              data-testid="chain-refused-row"
                            >
                              {row.startsAt.slice(0, 10)} · {row.message}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className={styles.bookerActions}>
                      <button
                        type="button"
                        className={styles.primary}
                        disabled={busy || !canBook}
                        onClick={book}
                        data-testid="book-save"
                      >
                        {isBlock
                          ? "Hold the chair"
                          : family.length > 0
                            ? `Book ${String(family.length + 1)} patients`
                            : "Book"}
                      </button>
                      <button
                        type="button"
                        className={styles.secondary}
                        onClick={() => {
                          setBooking(false);
                          clearBookingForm();
                        }}
                        data-testid="book-cancel"
                      >
                        Cancel
                      </button>

                      {/*
                  **Holding the chair, last** (CS7). It came first from
                  2026-09-07 because ticking it removes four fields; the client
                  moved it here knowingly, because most bookings have a patient
                  and the rare case should not be the first thing read. It is
                  still the same checkbox — ticking it here reshapes the form
                  above exactly as before, and every spec that drives this form
                  still does so with `.check()`.

                  A block is an appointment with nobody in it — the chair held
                  for maintenance, a meeting or a break — and it turns over at
                  zero, so another booking may sit flush against it.
                */}
                      <label className={styles.holdToggle}>
                        <input
                          type="checkbox"
                          checked={isBlock}
                          onChange={(event) => {
                            setIsBlock(event.target.checked);
                            setBlockReasonId("");
                            setPatient(null);
                            setPatientQuery("");
                          }}
                          data-testid="book-is-block"
                        />
                        Hold the chair instead
                        <HelpTip label="About holding the chair">
                          No patient — maintenance, a meeting, a break. Another booking may sit
                          flush against it.
                        </HelpTip>
                      </label>
                    </div>

                    {addingPatient && (
                      <NewPatientForm
                        onCreated={(created) => {
                          setAddingPatient(false);
                          if (addingFamily) {
                            setAddingFamily(false);
                            setFamily((members) => [
                              ...members,
                              {
                                patient: created,
                                procedureCodeIds: procedureCodeId === null ? [] : [procedureCodeId],
                              },
                            ]);
                            return;
                          }
                          setPatient(created);
                          setMatches([]);
                          setPatientQuery("");
                        }}
                        onCancel={() => setAddingPatient(false)}
                      />
                    )}
                  </Modal>
                )}

                {!booking && selected !== null && (
                  /*
              **On the combined calendar the panel works at the booking's own
              location** (CF-D15, CF-D9): its permissions, its currency, and
              every request it makes. Keyed by the booking, so nothing it read
              for one location is kept for the next.
            */
                  <BookingLocationProvider
                    value={
                      selectedLocation === undefined
                        ? null
                        : {
                            branchId: selectedLocation.branch.id,
                            name: selectedLocation.branch.name,
                            permissions: selectedLocation.branch.permissions,
                          }
                    }
                  >
                    <AppointmentPanel
                      key={combined ? selected.id : undefined}
                      appointment={selected}
                      ringCommand={ringCommand}
                      onRingTaken={() => setRingCommand(null)}
                      chairName={
                        (selectedLocation === undefined
                          ? chairs.find((chair) => chair.id === selected.chairId)?.name
                          : selectedLocation.chairs.find((chair) => chair.id === selected.chairId)
                              ?.name) ?? "—"
                      }
                      location={
                        selectedLocation === undefined
                          ? undefined
                          : {
                              name: selectedLocation.branch.name,
                              code: selectedLocation.branch.code,
                              hue: selectedLocation.branch.hue,
                            }
                      }
                      staff={staff}
                      codes={selectedLocation === undefined ? catalogue : panelCodes}
                      timezone={
                        selectedLocation?.branch.timezone ?? roster?.frame.timezone ?? timezone
                      }
                      onReschedule={() => setRescheduling(selected.id)}
                      onOpenAppointment={openAppointment}
                      onOpenFile={(patientId) => {
                        navigate(`/patients/${patientId}`);
                      }}
                      onStatusChanged={() => {
                        // A status change rewrites the block on the grid — its colour,
                        // and whether it holds the chair at all (a cancelled slot is
                        // free immediately, §6.1). It can also empty a waitlist entry,
                        // when the freed slot is offered to somebody waiting.
                        void reload();
                        setWaitlistVersion((count) => count + 1);
                      }}
                      onClose={() => setSelectedId(null)}
                    />
                  </BookingLocationProvider>
                )}
              </div>
            )}
        </div>
      </div>
    </div>
  );
}

/**
 * Whether the view on screen is already showing today, so "Today" can say so.
 *
 * **Each view answers it differently**, which is the whole reason this is a
 * function rather than `day === today()`: the week view is on today whenever
 * today falls anywhere inside the week it is drawing, and the month view cares
 * only about the month. Comparing the day alone would leave the button live on
 * a week view that is already showing this week.
 */
function showingToday(
  view: "day" | "week" | "month",
  day: string,
  monthKey: string,
  now: string,
): boolean {
  if (view === "month") return monthKey === now.slice(0, 7);
  if (view === "week") return weekStartOf(day) === weekStartOf(now);
  return day === now;
}

/**
 * `2026-03` shifted by a number of months, wrapping the year.
 *
 * Built from `Date.UTC` rather than string arithmetic, so December to January
 * is the same code path as March to April.
 */
function shiftMonth(month: string, by: number): string {
  const [year, index] = month.split("-").map(Number);
  const at = new Date(Date.UTC(year ?? 1970, (index ?? 1) - 1 + by, 1));
  return at.toISOString().slice(0, 7);
}

/**
 * The Sunday that starts the week a date falls in, as `YYYY-MM-DD`.
 *
 * **Sunday-first, because the mockup is** — it draws `Sun Mon Tue …` in both
 * the week header and the month grid. STORY-041 shipped this Monday-first on my
 * own reasoning about how a clinic plans; the mockup is the design spec and
 * wins, so it was corrected at STORY-042 rather than left as a disagreement
 * between two views of the same calendar.
 */
function weekStartOf(date: string): string {
  const at = new Date(`${date}T00:00:00.000Z`);
  return new Date(at.getTime() - at.getUTCDay() * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** `date` shifted by a number of days, wrapping months and years correctly. */
function shiftDay(date: string, byDays: number): string {
  const at = new Date(`${date}T00:00:00.000Z`);
  return new Date(at.getTime() + byDays * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/**
 * The header's big serif title, one shape per view — the mockup's `calTitle`.
 *
 * Built from `Date.UTC` throughout rather than parsing `date` with the local
 * timezone, so a reader west of UTC never sees the day before the one they
 * asked for.
 */
function titleFor(view: "day" | "week" | "month", day: string, monthKey: string): string {
  // `Intl` supplies correctly-localised names; the *order* of day, month and
  // year is fixed here rather than left to `Intl`'s own locale formatting —
  // otherwise a US-locale browser reads "August 9" where the mockup's own
  // convention, and everywhere else in this app, is day before month.
  const monthName = (at: Date): string =>
    new Intl.DateTimeFormat(undefined, { month: "long", timeZone: "UTC" }).format(at);
  const monthNameShort = (at: Date): string =>
    new Intl.DateTimeFormat(undefined, { month: "short", timeZone: "UTC" }).format(at);
  const weekdayName = (at: Date): string =>
    new Intl.DateTimeFormat(undefined, { weekday: "long", timeZone: "UTC" }).format(at);

  if (view === "month") {
    const [year, month] = monthKey.split("-").map(Number);
    const at = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, 1));
    return `${monthName(at)} ${String(year ?? 1970)}`;
  }

  if (view === "week") {
    const start = new Date(`${weekStartOf(day)}T00:00:00.000Z`);
    const end = new Date(start.getTime() + 6 * 24 * 60 * 60 * 1000);

    if (start.getUTCMonth() === end.getUTCMonth()) {
      return `${String(start.getUTCDate())} – ${String(end.getUTCDate())} ${monthName(end)}`;
    }

    return `${String(start.getUTCDate())} ${monthNameShort(start)} – ${String(end.getUTCDate())} ${monthName(end)}`;
  }

  const at = new Date(`${day}T00:00:00.000Z`);
  return `${weekdayName(at)}, ${String(at.getUTCDate())} ${monthName(at)}`;
}

/** Why a provider is not working today, in the picker's words (FX19). */
function awayLabel(away: "LEAVE" | "TRAINING" | "OFF" | null): string | undefined {
  if (away === "LEAVE") return "on leave";
  if (away === "TRAINING") return "at training";
  if (away === "OFF") return "off today";
  return undefined;
}

/**
 * `2026-09-25` → `Fri 25 Sep`, for the booking form's folded line (CS7). The
 * day is already the branch's own date, so it is read as a calendar date in
 * UTC rather than shifted through the browser's zone.
 */
function shortDate(iso: string): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        timeZone: "UTC",
      });
}
